import { useState, useEffect, useCallback } from 'react';
import API_CONFIG from '../../config/api';
import './AdminTabs.css';
import DigioSettingsModal from './DigioSettingsModal';
import KycDetailsModal from './KycDetailsModal';

export function AnalyticsTab({ adminSession }) {
    const [analytics, setAnalytics] = useState({
        userGrowth: [],
        loanStats: [],
        revenueData: [],
        kpi: { approvalRate: 0, avgTicket: 0, disbursed: 0, collectionRate: 0 }
    });
    const [timeframe, setTimeframe] = useState('30d');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/analytics`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) {
                const kpi = data.data?.kpi || {};
                setAnalytics({
                    userGrowth: data.data?.userGrowth || [],
                    loanStats: data.data?.loanStats || [],
                    revenueData: data.data?.revenueData || [],
                    kpi: {
                        approvalRate: kpi.approvalRate || 0,
                        avgTicket: kpi.avgTicket || 0,
                        disbursed: kpi.disbursed || 0,
                        collectionRate: kpi.collectionRate || 0
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
        }
        setLoading(false);
    };

    return (
        <div className="analytics-tab">
            <div className="super-card">
                <div className="super-card-header user-header">
                    <div className="header-left">
                        <h2>📈 Analytics & Insights</h2>
                        <p className="header-sub">Real-time view of conversion, disbursal, and collections.</p>
                    </div>
                    <div className="header-actions">
                        <select className="admin-form-select" value={timeframe} onChange={e => setTimeframe(e.target.value)}>
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                        </select>
                        <button className="secondary-btn" onClick={fetchAnalytics} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
                        <button className="export-btn" onClick={() => alert('Export coming soon')}>📊 Export</button>
                    </div>
                </div>
                <p className="tab-subtitle">Overview of platform growth, loan performance and revenue trends.</p>

                <div className="analytics-grid kpi-grid">
                    <div className="analytics-card kpi">
                        <h3>Approval Rate</h3>
                        <div className="stat-large">{analytics.kpi.approvalRate || 0}%</div>
                        <p>Loans approved / reviewed</p>
                    </div>
                    <div className="analytics-card kpi">
                        <h3>Avg Ticket</h3>
                        <div className="stat-large">₹{analytics.kpi.avgTicket?.toLocaleString?.('en-IN') || 0}</div>
                        <p>Per active loan</p>
                    </div>
                    <div className="analytics-card kpi">
                        <h3>Total Disbursed</h3>
                        <div className="stat-large">₹{analytics.kpi.disbursed?.toLocaleString?.('en-IN') || 0}</div>
                        <p>All time</p>
                    </div>
                    <div className="analytics-card kpi">
                        <h3>Collection Rate</h3>
                        <div className="stat-large">{analytics.kpi.collectionRate || 0}%</div>
                        <p>On-time repayments</p>
                    </div>
                </div>
                <div className="chart-placeholder">
                    <div className="chart-inner">Charts go here (conversion funnel, disbursal trend, collections)</div>
                </div>
            </div>
        </div>
    );
}

export function PermissionsTab({ adminSession }) {
    const [permissions, setPermissions] = useState([]);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        fetchPermissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPermissions = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/permissions`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) {
                setPermissions(data.data);
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    const handleDeletePermission = async (id) => {
        if (!window.confirm('Are you sure you want to delete this permission? This might break existing roles.')) return;
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/permissions/${id}`, {
                method: 'DELETE',
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) fetchPermissions();
            else alert(data.message || 'Delete failed');
        } catch (e) { alert('Server error'); }
    };

    const handleAddPermission = async () => {
        const name = prompt('Permission Name (e.g. users.view):');
        if (!name) return;
        const desc = prompt('Description:');
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/permissions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({ permission_name: name, description: desc })
            });
            const data = await res.json();
            if (data.success) fetchPermissions();
            else alert(data.message || 'Failed to add');
        } catch (e) { alert('Server error'); }
    };

    const [editingPermission, setEditingPermission] = useState(null);

    const handleUpdatePermission = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/permissions/${editingPermission.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({
                    permission_name: editingPermission.permission_name,
                    description: editingPermission.description
                })
            });
            const data = await res.json();
            if (data.success) {
                setEditingPermission(null);
                fetchPermissions();
            } else alert(data.message || 'Update failed');
        } catch (e) { alert('Server error'); }
    };

    return (
        <div className="permissions-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>🔑 Permissions Management</h2>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input className="search-input" placeholder="Search permissions" value={filter} onChange={e => setFilter(e.target.value)} />
                        <button className="primary-btn" onClick={handleAddPermission}>+ Add Permission</button>
                    </div>
                </div>
                <p className="tab-subtitle">Create and manage granular permissions used by roles and admins.</p>
                <div className="permissions-grid">
                    {permissions.filter(p => p.permission_name.toLowerCase().includes(filter.toLowerCase())).map(perm => (
                        <div key={perm.id} className="permission-card">
                            <h4>{perm.permission_name}</h4>
                            <p>{perm.description || 'No description'}</p>
                            <div className="permission-actions">
                                <button className="btn-small" onClick={() => setEditingPermission(perm)}>Edit</button>
                                <button className="btn-small danger" onClick={() => handleDeletePermission(perm.id)}>Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {editingPermission && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content">
                        <h3>Edit Permission</h3>
                        <form onSubmit={handleUpdatePermission}>
                            <div className="modal-form-group">
                                <label>Name</label>
                                <input
                                    className="modal-input"
                                    value={editingPermission.permission_name}
                                    onChange={e => setEditingPermission({ ...editingPermission, permission_name: e.target.value })}
                                />
                            </div>
                            <div className="modal-form-group">
                                <label>Description</label>
                                <textarea
                                    className="modal-input"
                                    value={editingPermission.description || ''}
                                    onChange={e => setEditingPermission({ ...editingPermission, description: e.target.value })}
                                />
                            </div>
                            <div className="admin-modal-actions">
                                <button type="button" className="admin-modal-btn cancel" onClick={() => setEditingPermission(null)}>Cancel</button>
                                <button type="submit" className="admin-modal-btn confirm">Update</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export function RolesTab({ adminSession }) {
    const [roles, setRoles] = useState([]);
    const [roleFilter, setRoleFilter] = useState('');

    useEffect(() => {
        fetchRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRoles = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/roles`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) {
                setRoles(data.data);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const handleDeleteRole = async (id) => {
        if (!window.confirm('Delete this role? Users with this role might lose access.')) return;
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/roles/${id}`, {
                method: 'DELETE',
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) fetchRoles();
            else alert(data.message || 'Delete failed');
        } catch (e) { alert('Server error'); }
    }

    const handleCreateRole = async () => {
        const name = prompt('Role Name (e.g. support_tier_1):');
        if (!name) return;
        const level = prompt('Level (1-100, Super Admin is 100):', '10');
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/roles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({ role_name: name, level: parseInt(level) })
            });
            const data = await res.json();
            if (data.success) fetchRoles();
            else alert(data.message || 'Failed to create');
        } catch (e) { alert('Server error'); }
    };

    const [editingRole, setEditingRole] = useState(null);
    const [allPermissions, setAllPermissions] = useState([]);

    const handleEditRole = async (role) => {
        try {
            // Fetch this role's specific permissions
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/roles/${role.id}/permissions`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();

            // Also need all available permissions for the checklists
            const allRes = await fetch(`${API_CONFIG.BASE_URL}/api/admin/permissions`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const allData = await allRes.json();

            setAllPermissions(allData.data || []);
            setEditingRole({
                ...role,
                permissions: data.data.map(p => p.permission_name)
            });
        } catch (e) { alert('Failed to load role details'); }
    };

    const togglePermission = (permName) => {
        setEditingRole(prev => {
            const current = prev.permissions || [];
            if (current.includes(permName)) {
                return { ...prev, permissions: current.filter(p => p !== permName) };
            } else {
                return { ...prev, permissions: [...current, permName] };
            }
        });
    };

    const handleUpdateRole = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/roles/${editingRole.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({
                    role_name: editingRole.role_name,
                    level: editingRole.level,
                    permissions: editingRole.permissions
                })
            });
            const data = await res.json();
            if (data.success) {
                setEditingRole(null);
                fetchRoles();
            } else alert(data.message || 'Update failed');
        } catch (e) { alert('Server error'); }
    };

    return (
        <div className="roles-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>👔 Role Management</h2>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input className="search-input" placeholder="Search roles" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} />
                        <button className="primary-btn" onClick={handleCreateRole}>+ Create Role</button>
                    </div>
                </div>
                <p className="tab-subtitle">Define roles and assign permission sets to streamline admin access.</p>
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Role Name</th>
                            <th>Level</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roles.filter(r => r.role_name.toLowerCase().includes(roleFilter.toLowerCase())).map(role => (
                            <tr key={role.id}>
                                <td><strong>{role.role_name}</strong></td>
                                <td>{role.level}</td>
                                <td>
                                    <button className="action-btn" onClick={() => handleEditRole(role)}>✏️</button>
                                    {role.role_name !== 'super_admin' && (
                                        <button className="action-btn" onClick={() => handleDeleteRole(role.id)}>🗑️</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editingRole && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content" style={{ maxWidth: '600px' }}>
                        <h3>Edit Role: {editingRole.role_name}</h3>
                        <form onSubmit={handleUpdateRole}>
                            <div className="modal-form-group">
                                <label>Level (Security Clearance)</label>
                                <input
                                    type="number"
                                    className="modal-input"
                                    value={editingRole.level}
                                    onChange={e => setEditingRole({ ...editingRole, level: parseInt(e.target.value) })}
                                />
                            </div>

                            <div className="modal-form-group">
                                <label>Granular Permissions</label>
                                <div className="permissions-checklist" style={{
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '10px',
                                    padding: '10px',
                                    background: '#f9f9f9',
                                    borderRadius: '6px'
                                }}>
                                    {allPermissions.map(p => (
                                        <label key={p.id} className="checklist-item" style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '13px'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={editingRole.permissions?.includes(p.permission_name)}
                                                onChange={() => togglePermission(p.permission_name)}
                                            />
                                            {p.permission_name}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="admin-modal-actions">
                                <button type="button" className="admin-modal-btn cancel" onClick={() => setEditingRole(null)}>Cancel</button>
                                <button type="submit" className="admin-modal-btn confirm">Save Role</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export function SettingsTab({ adminSession }) {
    const [settings, setSettings] = useState({
        site_name: 'E-Fin Admin',
        maintenance_mode: 'false',
        smtp_host: '',
        smtp_port: '587'
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/settings`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) setSettings(prev => ({ ...prev, ...data.data }));
        } catch (e) { console.error('Settings fetch error:', e); }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/settings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({ settings })
            });
            const data = await res.json();
            if (data.success) alert('Settings saved successfully');
        } catch (e) { alert('Failed to save settings'); }
        setIsSaving(false);
    };

    return (
        <div className="settings-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>⚙️ System Settings</h2>
                    <button className="primary-btn" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
                <div className="settings-sections">
                    <div className="setting-section">
                        <h3>General Settings</h3>
                        <div className="setting-item">
                            <label>Site Name</label>
                            <input
                                type="text"
                                value={settings.site_name}
                                onChange={e => setSettings({ ...settings, site_name: e.target.value })}
                                className="setting-input"
                            />
                        </div>
                        <div className="setting-item">
                            <label>Maintenance Mode</label>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={settings.maintenance_mode === 'true'}
                                    onChange={e => setSettings({ ...settings, maintenance_mode: e.target.checked ? 'true' : 'false' })}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>
                    <div className="setting-section">
                        <h3>Email Configuration</h3>
                        <div className="setting-item">
                            <label>SMTP Host</label>
                            <input
                                type="text"
                                value={settings.smtp_host}
                                onChange={e => setSettings({ ...settings, smtp_host: e.target.value })}
                                className="setting-input"
                            />
                        </div>
                        <div className="setting-item">
                            <label>SMTP Port</label>
                            <input
                                type="number"
                                value={settings.smtp_port}
                                onChange={e => setSettings({ ...settings, smtp_port: e.target.value })}
                                className="setting-input"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function DatabaseTab({ adminSession }) {
    const [dbStats, setDbStats] = useState({
        totalTables: 0,
        databaseSize: '0 MB'
    });
    const [tables, setTables] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [sortBy, setSortBy] = useState('rows');
    const [structureModal, setStructureModal] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
        if (!uid) return;
        fetchDbData(uid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminSession?.userId]);

    const fetchDbData = async (forcedUid) => {
        setIsLoading(true);
        try {
            const uid = forcedUid || adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
            if (!uid) throw new Error('Missing admin session');
            const [statsRes, tablesRes] = await Promise.all([
                fetch(`${API_CONFIG.BASE_URL}/api/admin/database/stats`, { headers: { 'x-user-id': uid } }),
                fetch(`${API_CONFIG.BASE_URL}/api/admin/database/tables`, { headers: { 'x-user-id': uid } })
            ]);

            const statsData = await statsRes.json();
            const tablesData = await tablesRes.json();

            if (statsData.success) setDbStats(statsData.data);
            if (tablesData.success) setTables(tablesData.data);
        } catch (e) {
            console.error('DB fetch error:', e);
        }
        setIsLoading(false);
    };

    const totalRows = tables.reduce((sum, t) => sum + (t.count || 0), 0);
    const largest = tables.slice().sort((a, b) => (b.count || 0) - (a.count || 0))[0];

    const filteredTables = tables
        .filter(t => t.name.toLowerCase().includes(filter.toLowerCase()))
        .sort((a, b) => {
            if (sortBy === 'name') return a.name.localeCompare(b.name);
            return (b.count || 0) - (a.count || 0);
        });

    const exportSnapshot = () => {
        const payload = { fetchedAt: new Date().toISOString(), stats: dbStats, tables };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'db_snapshot.json';
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleExportTable = async (name) => {
        try {
            const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/database/tables/${encodeURIComponent(name)}/export`, {
                method: 'POST',
                headers: { 'x-user-id': uid }
            });
            if (!res.ok) {
                const msg = await res.text();
                return alert(`Export failed (${res.status}): ${msg || 'Unknown error'}`);
            }
            const data = await res.json();
            if (!data.success) return alert(data.message || 'Export failed');
            const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${name}_export.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export error', e);
            alert('Export failed. Check console/network for details.');
        }
    };

    const handleViewStructure = async (name) => {
        setActionLoading(true);
        try {
            const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/database/tables/${encodeURIComponent(name)}/info`, {
                headers: { 'x-user-id': uid }
            });
            if (!res.ok) {
                const msg = await res.text();
                alert(`Structure load failed (${res.status}): ${msg || 'Unknown error'}`);
                setActionLoading(false);
                return;
            }
            const data = await res.json();
            if (!data.success) {
                alert(data.message || 'Could not load structure');
                setActionLoading(false);
                return;
            }
            setStructureModal({
                name,
                columns: data.data.columns || [],
                sample: data.data.sample || []
            });
        } catch (e) {
            console.error('Structure error', e);
            alert('Failed to load structure. See console.');
        }
        setActionLoading(false);
    };

    return (
        <div className="database-tab">
            <div className="super-card">
                <div className="super-card-header user-header">
                    <div className="header-left">
                        <h2>💾 Database Command Center</h2>
                        <p className="header-sub">Monitor size, rows, and table inventory in one place.</p>
                    </div>
                    <div className="header-actions">
                        <button className="secondary-btn" onClick={() => fetchDbData()} disabled={isLoading}>Refresh</button>
                        <button className="secondary-btn" onClick={exportSnapshot}>Export Snapshot</button>
                        <button className="primary-btn" onClick={async () => {
                            setActionLoading(true);
                            try {
                                const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
                                await fetch(`${API_CONFIG.BASE_URL}/api/admin/database/backup`, { method: 'POST', headers: { 'x-user-id': uid } });
                                alert('Backup started');
                            } catch { alert('Backup failed'); }
                            setActionLoading(false);
                        }}>Backup Now</button>
                    </div>
                </div>

                <div className="db-stats-grid">
                    <div className="db-stat-card">
                        <h3>Total Tables</h3>
                        <div className="stat-value">{dbStats.totalTables}</div>
                    </div>
                    <div className="db-stat-card">
                        <h3>Total Rows</h3>
                        <div className="stat-value">{totalRows.toLocaleString()}</div>
                    </div>
                    <div className="db-stat-card">
                        <h3>Database Size</h3>
                        <div className="stat-value">{dbStats.databaseSize}</div>
                    </div>
                    <div className="db-stat-card">
                        <h3>Largest Table</h3>
                        <div className="stat-value">{largest ? largest.name : '—'}</div>
                    </div>
                </div>

                <div className="db-controls">
                    <div className="db-filter-group">
                        <input
                            className="search-input"
                            placeholder="Search tables..."
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                        />
                        <select className="admin-form-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                            <option value="rows">Sort by rows</option>
                            <option value="name">Sort by name</option>
                        </select>
                    </div>
                    <div className="db-maintenance">
                        <button className="action-header-btn" disabled={actionLoading} onClick={async () => {
                            setActionLoading(true);
                            const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
                            await Promise.all(filteredTables.map(t => fetch(`${API_CONFIG.BASE_URL}/api/admin/database/tables/${t.name}/analyze`, { method: 'POST', headers: { 'x-user-id': uid } })));
                            setActionLoading(false);
                            alert('Analyze complete');
                        }}>Analyze</button>
                        <button className="action-header-btn" disabled={actionLoading} onClick={async () => {
                            setActionLoading(true);
                            const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
                            await Promise.all(filteredTables.map(t => fetch(`${API_CONFIG.BASE_URL}/api/admin/database/tables/${t.name}/optimize`, { method: 'POST', headers: { 'x-user-id': uid } })));
                            setActionLoading(false);
                            alert('Optimize complete');
                        }}>Optimize</button>
                        <button className="action-header-btn" disabled={actionLoading} onClick={async () => {
                            setActionLoading(true);
                            const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
                            await fetch(`${API_CONFIG.BASE_URL}/api/admin/database/cache/flush`, { method: 'POST', headers: { 'x-user-id': uid } });
                            setActionLoading(false);
                            alert('Cache flushed');
                        }}>Clear Cache</button>
                    </div>
                </div>

                <div className="db-tables-list">
                    <h3>Database Tables</h3>
                    <div className="super-table-container">
                        <table className="super-table">
                            <thead>
                                <tr>
                                    <th>Table Name</th>
                                    <th>Records</th>
                                    <th>% of Total Rows</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="4">Analyzing data structure...</td></tr>
                                ) : (
                                    filteredTables.map(t => {
                                        const pct = totalRows ? ((t.count / totalRows) * 100).toFixed(1) : '0.0';
                                        return (
                                            <tr key={t.name}>
                                                <td><strong>{t.name}</strong></td>
                                                <td>{t.count.toLocaleString()}</td>
                                                <td>{pct}%</td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button className="action-btn view" onClick={() => handleViewStructure(t.name)}>Structure</button>
                                                        <button className="action-btn edit" onClick={() => handleExportTable(t.name)}>Export</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {structureModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content" style={{ maxWidth: '720px' }}>
                        <h3>Structure: {structureModal.name}</h3>
                        <div className="super-table-container">
                            <table className="super-table">
                                <thead>
                                    <tr>
                                        <th>Column</th>
                                        <th>Type</th>
                                        <th>Nullable</th>
                                        <th>Key</th>
                                        <th>Default</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {structureModal.columns.map(col => (
                                        <tr key={col.name}>
                                            <td>{col.name}</td>
                                            <td>{col.type}</td>
                                            <td>{col.nullable}</td>
                                            <td>{col.col_key || '—'}</td>
                                            <td>{col.col_default || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <h4 style={{ marginTop: '12px' }}>Sample (first {structureModal.sample.length} rows)</h4>
                        <pre style={{ maxHeight: '220px', overflow: 'auto', background: '#f8fafc', padding: '10px', borderRadius: '8px' }}>
                            {JSON.stringify(structureModal.sample, null, 2)}
                        </pre>
                        <div className="admin-modal-actions">
                            <button className="admin-modal-btn cancel" onClick={() => setStructureModal(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function SecurityTab() {
    return (
        <div className="security-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>🛡️ Security Center</h2>
                </div>
                <div className="security-sections">
                    <div className="security-section">
                        <h3>Password Policy</h3>
                        <div className="setting-item">
                            <label>Minimum Length</label>
                            <input type="number" defaultValue="8" className="setting-input" />
                        </div>
                        <div className="setting-item">
                            <label>Require Special Characters</label>
                            <label className="toggle-switch">
                                <input type="checkbox" defaultChecked />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>
                    <div className="security-section">
                        <h3>Two-Factor Authentication</h3>
                        <div className="setting-item">
                            <label>Enable 2FA</label>
                            <label className="toggle-switch">
                                <input type="checkbox" />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>
                    <div className="security-section">
                        <h3>Session Management</h3>
                        <div className="setting-item">
                            <label>Session Timeout (minutes)</label>
                            <input type="number" defaultValue="60" className="setting-input" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function ActivityMonitorTab({ adminSession }) {
    const [activities, setActivities] = useState([]);
    const [filter, setFilter] = useState('');
    const [selectedActivity, setSelectedActivity] = useState(null);

    useEffect(() => {
        fetchActivities();
        const interval = setInterval(fetchActivities, 5000); // Refresh every 5 seconds
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchActivities = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/activity`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) {
                setActivities(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
        }
    };

    const filtered = activities.filter(a => {
        const needle = filter.toLowerCase();
        return !needle ||
            (a.description || '').toLowerCase().includes(needle) ||
            (a.action_type || '').toLowerCase().includes(needle) ||
            (a.admin_name || '').toLowerCase().includes(needle) ||
            (a.admin_email || '').toLowerCase().includes(needle);
    });

    const grouped = filtered.reduce((acc, a) => {
        const key = a.admin_name || a.admin_email || 'Unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(a);
        return acc;
    }, {});

    const uniqueActors = Object.keys(grouped).length;
    const lastEvent = activities[0]?.created_at;

    return (
        <div className="activity-monitor-tab">
            <div className="super-card">
                <div className="super-card-header user-header">
                    <div className="header-left">
                        <h2>📡 Activity Monitor</h2>
                        <p className="header-sub">Live admin actions grouped by user.</p>
                    </div>
                    <div className="header-actions">
                        <input
                            className="search-input"
                            placeholder="Search user, email, action..."
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                        />
                        <span className="live-badge">🔴 LIVE</span>
                    </div>
                </div>
                <div className="activity-stats">
                    <div className="activity-stat">
                        <h4>Events (last 200)</h4>
                        <div className="stat-value">{activities.length}</div>
                    </div>
                    <div className="activity-stat">
                        <h4>Unique Admins</h4>
                        <div className="stat-value">{uniqueActors}</div>
                    </div>
                    <div className="activity-stat">
                        <h4>Last Event</h4>
                        <div className="stat-value">{lastEvent ? new Date(lastEvent).toLocaleTimeString() : '—'}</div>
                    </div>
                </div>
                <div className="activity-feed">
                    <h3>Recent Activity by Admin</h3>
                    {Object.keys(grouped).length === 0 && <p className="no-data">No recent activity</p>}
                    {Object.entries(grouped).map(([actor, events]) => (
                        <div key={actor} className="activity-group">
                            <div className="activity-group-header">
                                <strong>{actor}</strong>
                                <span className="badge">{events.length} events</span>
                            </div>
                            {events.slice(0, 5).map((activity, idx) => (
                                <div key={idx} className="activity-item" style={{ cursor: 'pointer' }} onClick={() => setSelectedActivity(activity)}>
                                    <span className="activity-icon">🡒</span>
                                    <div className="activity-content">
                                        <p><strong>{activity.action_type}</strong> — {activity.description}</p>
                                        <span className="activity-time">{new Date(activity.created_at).toLocaleString()}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {selectedActivity && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content" style={{ maxWidth: '520px' }}>
                        <h3>Activity Detail</h3>
                        <p><strong>Actor:</strong> {selectedActivity.admin_name || selectedActivity.admin_email || 'Unknown'}</p>
                        <p><strong>Action:</strong> {selectedActivity.action_type}</p>
                        <p><strong>Description:</strong> {selectedActivity.description}</p>
                        <p><strong>When:</strong> {new Date(selectedActivity.created_at).toLocaleString()}</p>
                        {(selectedActivity.target_type || selectedActivity.target_id) && (
                            <p>
                                <strong>Target:</strong> {selectedActivity.target_type || '—'} #{selectedActivity.target_id || '—'}
                            </p>
                        )}
                        <div className="admin-modal-actions">
                            <button className="admin-modal-btn cancel" onClick={() => setSelectedActivity(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function ReportsTab() {
    return (
        <div className="reports-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>📋 Reports & Analytics</h2>
                    <button className="primary-btn">Generate Report</button>
                </div>
                <div className="reports-grid">
                    <div className="report-card">
                        <h3>📊 User Report</h3>
                        <p>Generate comprehensive user statistics</p>
                        <button className="secondary-btn">Generate</button>
                    </div>
                    <div className="report-card">
                        <h3>💰 Financial Report</h3>
                        <p>Loan disbursements and revenue</p>
                        <button className="secondary-btn">Generate</button>
                    </div>
                    <div className="report-card">
                        <h3>📈 Activity Report</h3>
                        <p>System usage and activity logs</p>
                        <button className="secondary-btn">Generate</button>
                    </div>
                    <div className="report-card">
                        <h3>🔒 Security Report</h3>
                        <p>Login attempts and security events</p>
                        <button className="secondary-btn">Generate</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function TemplatesTab() {
    return (
        <div className="templates-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>✉️ Email & Message Templates</h2>
                    <button className="primary-btn">+ Create Template</button>
                </div>
                <p className="muted">Manage system email and notification templates.</p>
            </div>
        </div>
    );
}

export function SupportTab({ adminSession }) {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/support/tickets`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) setTickets(data.data || []);
        } catch (e) {
            console.error('Error fetching tickets:', e);
        }
        setLoading(false);
    };

    const assignTicket = async (ticketId) => {
        const adminId = prompt('Enter admin user ID to assign this ticket to:');
        if (!adminId) return;
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/support/assign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({ ticketId, adminId })
            });
            const data = await res.json();
            if (data.success) fetchTickets();
            else alert(data.message || 'Failed to assign ticket');
        } catch (e) {
            alert('Server error while assigning ticket');
        }
    };

    const resolveTicket = async (ticketId) => {
        if (!window.confirm('Mark ticket as resolved?')) return;
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/support/resolve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({ ticketId })
            });
            const data = await res.json();
            if (data.success) fetchTickets();
            else alert(data.message || 'Failed to resolve ticket');
        } catch (e) {
            alert('Server error while resolving ticket');
        }
    };

    return (
        <div className="support-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>🎫 Support Tickets</h2>
                    <button className="primary-btn" onClick={fetchTickets}>Refresh</button>
                </div>

                {loading ? (
                    <p>Loading tickets…</p>
                ) : (
                    <div className="super-table-container">
                        <table className="super-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Subject</th>
                                    <th>User</th>
                                    <th>Priority</th>
                                    <th>Status</th>
                                    <th>Assigned</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.map(t => (
                                    <tr key={t.id}>
                                        <td>{t.id}</td>
                                        <td>{t.subject}</td>
                                        <td>{t.userName}</td>
                                        <td>{t.priority}</td>
                                        <td>{t.status}</td>
                                        <td>{t.assignedName || '-'}</td>
                                        <td>
                                            <div className="action-buttons">
                                                <button className="action-btn edit" onClick={() => assignTicket(t.id)}>Assign</button>
                                                <button className="action-btn view" onClick={() => alert(t.description)}>View</button>
                                                {t.status !== 'Resolved' && (
                                                    <button className="action-btn confirm" onClick={() => resolveTicket(t.id)}>Resolve</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export function FinancialTab() {
    return (
        <div className="financial-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>💱 Financials</h2>
                    <button className="primary-btn">Export Financial Report</button>
                </div>
                <p className="muted">View and export financial reports and analytics.</p>
            </div>
        </div>
    );
}

export function ApiManagementTab({ adminSession }) {
    const [keys, setKeys] = useState([]);
    const [creating, setCreating] = useState(false);
    const [newKey, setNewKey] = useState({ name: '', scope: 'public', prefix: 'pub' });
    const [usage, setUsage] = useState({ today: 0, errors: 0, successRate: 0, avgLatency: 0 });
    const [rateLimit, setRateLimit] = useState({ enabled: true, limit: 600, window: '1m' });
    const [isLoading, setIsLoading] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const adminEndpoints = [
        { method: 'POST', path: '/api/admin/login', desc: 'Admin authentication' },
        { method: 'GET', path: '/api/admin/users', desc: 'List users' },
        { method: 'POST', path: '/api/admin/create-user', desc: 'Create user' },
        { method: 'PUT', path: '/api/admin/users/:id', desc: 'Update user' },
        { method: 'DELETE', path: '/api/admin/users/:id', desc: 'Delete user' },
        { method: 'POST', path: '/api/admin/users/:id/expire-password', desc: 'Expire user password' },
        { method: 'POST', path: '/api/admin/users/:id/restrict', desc: 'Restrict user' },
        { method: 'GET', path: '/api/admin/admins', desc: 'List admins' },
        { method: 'POST', path: '/api/admin/admins', desc: 'Create admin' },
        { method: 'PUT', path: '/api/admin/admins/:id', desc: 'Update admin' },
        { method: 'DELETE', path: '/api/admin/admins/:id', desc: 'Delete admin' },
        { method: 'GET', path: '/api/admin/roles', desc: 'List roles' },
        { method: 'GET', path: '/api/admin/permissions', desc: 'List permissions' },
        { method: 'GET', path: '/api/admin/loans', desc: 'List loans' },
        { method: 'PUT', path: '/api/admin/loans/:id', desc: 'Update loan' },
        { method: 'DELETE', path: '/api/admin/loans/:id', desc: 'Delete loan' },
        { method: 'GET', path: '/api/admin/leads', desc: 'List leads' },
        { method: 'POST', path: '/api/admin/leads', desc: 'Create lead' },
        { method: 'PUT', path: '/api/admin/leads/:id', desc: 'Update lead' },
        { method: 'DELETE', path: '/api/admin/leads/:id', desc: 'Delete lead' },
        { method: 'GET', path: '/api/admin/settings', desc: 'Read system settings' },
        { method: 'POST', path: '/api/admin/settings', desc: 'Update system settings' },
        { method: 'GET', path: '/api/admin/api-keys', desc: 'List API keys' },
        { method: 'POST', path: '/api/admin/api-keys', desc: 'Create API key' },
        { method: 'PUT', path: '/api/admin/api-keys/:id', desc: 'Update/rotate API key' },
        { method: 'DELETE', path: '/api/admin/api-keys/:id', desc: 'Delete API key' },
        { method: 'GET', path: '/api/admin/api-keys/usage', desc: 'API usage metrics' },
        { method: 'GET', path: '/api/admin/analytics', desc: 'Analytics summary' },
        { method: 'GET', path: '/api/admin/activity', desc: 'Activity logs' },
        { method: 'GET', path: '/api/admin/support/tickets', desc: 'Support tickets' }
    ];

    const fetchKeys = async () => {
        setIsLoading(true);
        setErrorMsg('');
        try {
            const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
            if (!uid) throw new Error('Missing admin session');
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/api-keys`, {
                headers: { 'x-user-id': uid }
            });
            if (!res.ok) {
                setErrorMsg(`API keys fetch failed (${res.status}). Re-login as super admin and retry.`);
                return;
            }
            const data = await res.json();
            if (data.success) setKeys(data.data || []);
            else {
                setErrorMsg(data.message || 'API keys fetch error');
                console.error('api-keys error', data);
            }
        } catch (e) {
            console.error('API keys fetch error', e);
            setErrorMsg('Unable to reach backend. Is it running?');
        }
        setIsLoading(false);
    };

    const fetchUsage = async () => {
        try {
            const uid = adminSession?.userId || JSON.parse(localStorage.getItem('adminSession') || '{}').userId;
            if (!uid) throw new Error('Missing admin session');
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/api-keys/usage`, {
                headers: { 'x-user-id': uid }
            });
            const data = await res.json();
            if (data.success) setUsage(data.data);
        } catch (e) { console.error('API usage fetch error', e); }
    };

    useEffect(() => {
        if (!adminSession?.userId) return;
        fetchKeys();
        fetchUsage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminSession?.userId]);

    const handleCreate = async () => {
        if (!newKey.name) return alert('Key name required');
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/api-keys`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify(newKey)
            });
            const data = await res.json();
            if (data.success) {
                setKeys(prev => [data.data, ...prev]);
                setCreating(false);
                setNewKey({ name: '', scope: 'public', prefix: 'pub' });
            } else alert(data.message || 'Create failed');
        } catch (e) {
            alert('Server error creating key');
        }
    };

    const handleRotate = async (id) => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/api-keys/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({ rotate: true })
            });
            const data = await res.json();
            if (data.success) {
                setKeys(prev => prev.map(k => k.id === id ? data.data : k));
            } else alert(data.message || 'Rotate failed');
        } catch (e) { alert('Server error rotating key'); }
    };

    const handleToggle = async (id, nextActive) => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/api-keys/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify({ active: nextActive })
            });
            const data = await res.json();
            if (data.success) {
                setKeys(prev => prev.map(k => k.id === id ? data.data : k));
            } else alert(data.message || 'Update failed');
        } catch (e) { alert('Server error updating key'); }
    };

    const copyKey = (value) => {
        navigator.clipboard.writeText(value).then(() => alert('Copied to clipboard'));
    };

    const openAccessModal = (key) => {
        setEditingKey({
            ...key,
            ip_allowlist: key.ip_allowlist?.join('\n') || ''
        });
    };

    const saveAccess = async () => {
        if (!editingKey) return;
        try {
            const payload = {
                allowed_from: editingKey.allowed_from || '00:00',
                allowed_to: editingKey.allowed_to || '23:59',
                ip_allowlist: editingKey.ip_allowlist
                    .split('\n')
                    .map(l => l.trim())
                    .filter(Boolean),
                rate_limit: Number(editingKey.rate_limit) || 0
            };

            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/api-keys/${editingKey.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                setKeys(prev => prev.map(k => k.id === editingKey.id ? data.data : k));
                setEditingKey(null);
            } else alert(data.message || 'Update failed');
        } catch (e) {
            alert('Server error saving access controls');
        }
    };

    return (
        <div className="api-tab">
            <div className="super-card">
                <div className="super-card-header user-header">
                    <div className="header-left">
                        <h2>🔌 API Management</h2>
                        <p className="header-sub">Create, rotate, and govern API access in one place.</p>
                    </div>
                    <div className="header-actions">
                        <button className="secondary-btn" onClick={() => setCreating(true)}>Create API Key</button>
                        <button className="secondary-btn" onClick={fetchKeys} disabled={isLoading}>Refresh</button>
                    </div>
                </div>

                {errorMsg && <div className="alert warning">{errorMsg}</div>}

                <div className="api-metrics">
                    <div className="api-metric">
                        <span>Requests Today</span>
                        <strong>{usage.today.toLocaleString()}</strong>
                    </div>
                    <div className="api-metric">
                        <span>Error Count</span>
                        <strong>{usage.errors}</strong>
                    </div>
                    <div className="api-metric">
                        <span>Success Rate</span>
                        <strong>{usage.successRate}%</strong>
                    </div>
                    <div className="api-metric">
                        <span>Avg Latency</span>
                        <strong>{usage.avgLatency} ms</strong>
                    </div>
                </div>

                <div className="api-rate-limit">
                    <div>
                        <h3>Rate Limiting</h3>
                        <p className="muted">Protect APIs from abuse by throttling requests.</p>
                    </div>
                    <div className="rate-controls">
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={rateLimit.enabled}
                                onChange={e => setRateLimit({ ...rateLimit, enabled: e.target.checked })}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                        <input
                            className="admin-form-input"
                            type="number"
                            value={rateLimit.limit}
                            onChange={e => setRateLimit({ ...rateLimit, limit: Number(e.target.value) })}
                            style={{ width: 120 }}
                        />
                        <select
                            className="admin-form-select"
                            value={rateLimit.window}
                            onChange={e => setRateLimit({ ...rateLimit, window: e.target.value })}
                            style={{ width: 120 }}
                        >
                            <option value="30s">30s</option>
                            <option value="1m">1 minute</option>
                            <option value="5m">5 minutes</option>
                            <option value="1h">1 hour</option>
                        </select>
                        <span className="muted">requests per window</span>
                    </div>
                </div>

                <div className="super-table-container">
                    <table className="super-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Key</th>
                                <th>Scope</th>
                                <th>Status</th>
                                <th>Last Used</th>
                                <th>Requests</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map(k => (
                                <tr key={k.id}>
                                    <td>{k.name}</td>
                                    <td>
                                        <code className="small-mono">{k.value}</code>
                                    </td>
                                    <td>{k.scope}</td>
                                    <td>
                                        <span className={`status-badge ${k.active ? 'status-success' : 'status-danger'}`}>
                                            {k.active ? 'Active' : 'Disabled'}
                                        </span>
                                    </td>
                                    <td>{k.lastUsed}</td>
                                    <td>{k.requests.toLocaleString()}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button className="action-btn view" onClick={() => copyKey(k.value)}>Copy</button>
                                            <button className="action-btn edit" onClick={() => handleRotate(k.id)}>Rotate</button>
                                            <button className="action-btn delete" onClick={() => handleToggle(k.id, !k.active)}>
                                                {k.active ? 'Disable' : 'Enable'}
                                            </button>
                                            <button className="action-btn view" onClick={() => openAccessModal(k)}>Access</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="api-webhooks">
                    <div className="api-webhook-card">
                        <h3>Webhook Endpoint</h3>
                        <p className="muted">Send payment and KYC events to your services.</p>
                        <div className="form-grid two-col">
                            <div className="form-group">
                                <label>URL</label>
                                <input className="admin-form-input" placeholder="https://example.com/webhooks/payu" />
                            </div>
                            <div className="form-group">
                                <label>Secret</label>
                                <input className="admin-form-input" placeholder="Use to verify signatures" />
                            </div>
                        </div>
                        <div className="webhook-actions">
                            <label className="toggle-switch">
                                <input type="checkbox" defaultChecked />
                                <span className="toggle-slider"></span>
                            </label>
                            <span className="muted">Webhook enabled</span>
                            <button className="secondary-btn">Save Webhook</button>
                        </div>
                    </div>
                </div>

                <div className="super-table-container" style={{ marginTop: '10px' }}>
                    <h3>Backend Endpoints</h3>
                    <table className="super-table">
                        <thead>
                            <tr>
                                <th>Method</th>
                                <th>Path</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {adminEndpoints.map(ep => (
                                <tr key={`${ep.method}-${ep.path}`}>
                                    <td><span className="status-badge status-success">{ep.method}</span></td>
                                    <td className="small-mono">{ep.path}</td>
                                    <td>{ep.desc}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {creating && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content" style={{ maxWidth: '520px' }}>
                        <h3>Create API Key</h3>
                        <div className="modal-form-group">
                            <label>Name</label>
                            <input className="modal-input" value={newKey.name} onChange={e => setNewKey({ ...newKey, name: e.target.value })} />
                        </div>
                        <div className="modal-form-group">
                            <label>Scope</label>
                            <input className="modal-input" value={newKey.scope} onChange={e => setNewKey({ ...newKey, scope: e.target.value })} placeholder="e.g. payments:write users:read" />
                        </div>
                        <div className="modal-form-group">
                            <label>Key Prefix</label>
                            <input className="modal-input" value={newKey.prefix} onChange={e => setNewKey({ ...newKey, prefix: e.target.value })} />
                        </div>
                        <div className="admin-modal-actions">
                            <button className="admin-modal-btn cancel" onClick={() => setCreating(false)}>Cancel</button>
                            <button className="admin-modal-btn confirm" onClick={handleCreate}>Generate Key</button>
                        </div>
                    </div>
                </div>
            )}

            {editingKey && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content" style={{ maxWidth: '520px' }}>
                        <h3>Access Controls</h3>
                        <div className="modal-form-group">
                            <label>Allowed From (HH:MM)</label>
                            <input
                                className="modal-input"
                                value={editingKey.allowed_from || '00:00'}
                                onChange={e => setEditingKey({ ...editingKey, allowed_from: e.target.value })}
                            />
                        </div>
                        <div className="modal-form-group">
                            <label>Allowed To (HH:MM)</label>
                            <input
                                className="modal-input"
                                value={editingKey.allowed_to || '23:59'}
                                onChange={e => setEditingKey({ ...editingKey, allowed_to: e.target.value })}
                            />
                        </div>
                        <div className="modal-form-group">
                            <label>Rate Limit (req/min)</label>
                            <input
                                className="modal-input"
                                type="number"
                                value={editingKey.rate_limit || 0}
                                onChange={e => setEditingKey({ ...editingKey, rate_limit: e.target.value })}
                            />
                        </div>
                        <div className="modal-form-group">
                            <label>IP Allowlist (one per line)</label>
                            <textarea
                                className="modal-input"
                                rows="4"
                                value={editingKey.ip_allowlist}
                                onChange={e => setEditingKey({ ...editingKey, ip_allowlist: e.target.value })}
                            />
                        </div>
                        <div className="admin-modal-actions">
                            <button className="admin-modal-btn cancel" onClick={() => setEditingKey(null)}>Cancel</button>
                            <button className="admin-modal-btn confirm" onClick={saveAccess}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function NotificationsTab() {
    return (
        <div className="notifications-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>🔔 Notifications</h2>
                    <button className="primary-btn">Send Broadcast</button>
                </div>
                <p className="muted">Manage system notifications and broadcasts.</p>
            </div>
        </div>
    );
}

export function HealthTab() {
    return (
        <div className="health-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <h2>📡 System Health</h2>
                    <button className="secondary-btn">Run Health Check</button>
                </div>
                <p className="muted">View system health metrics and monitoring tools.</p>
            </div>
        </div>
    );
}

export function PaymentGatewayTab({ adminSession }) {
    const [payuConfig, setPayuConfig] = useState({
        key: '',
        salt: '',
        merchantId: '',
        env: 'test',
        endpoint: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [lastTest, setLastTest] = useState(null);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/settings`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) {
                setPayuConfig(prev => ({
                    ...prev,
                    key: data.data['payu.key'] || '',
                    salt: data.data['payu.salt'] || '',
                    merchantId: data.data['payu.merchantId'] || '',
                    env: data.data['payu.env'] || 'test',
                    endpoint: data.data['payu.endpoint'] || 'https://secure.payu.in/_payment'
                }));
            }
        } catch (e) {
            console.error('Payment settings fetch error:', e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = {
                'payu.key': payuConfig.key,
                'payu.salt': payuConfig.salt,
                'payu.merchantId': payuConfig.merchantId,
                'payu.env': payuConfig.env,
                'payu.endpoint': payuConfig.endpoint
            };

            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify({ settings: payload })
            });
            const data = await res.json();
            if (data.success) {
                alert('PayU configuration saved');
            } else {
                alert(data.message || 'Save failed');
            }
        } catch (e) {
            alert('Server error while saving PayU config');
        } finally {
            setIsSaving(false);
        }
    };

    const handleTestConnection = () => {
        const ok = payuConfig.key && payuConfig.salt && payuConfig.merchantId;
        setLastTest({
            at: new Date().toLocaleString(),
            status: ok ? 'success' : 'failed',
            message: ok ? 'Credentials look valid (static check).' : 'Missing required fields.'
        });
    };

    const gatewayActive = payuConfig.key && payuConfig.salt;

    const isSuperAdmin = adminSession?.role === 'super_admin';
    const hasPermission = (perm) => {
        if (isSuperAdmin) return true;
        const userPerms = adminSession?.permissions || [];
        if (Array.isArray(perm)) return perm.some(p => userPerms.includes(p));
        return userPerms.includes(perm);
    };

    return (
        <div className="payment-gateway-tab">
            <div className="super-card">
                {isSuperAdmin && (
                    <>
                        <div className="super-card-header user-header">
                            <div className="header-left">
                                <h2>💳 Payment Gateway Management</h2>
                                <p className="header-sub">Configured for PayU. Manage keys, environment, and uptime checks.</p>
                            </div>
                            <div className="header-actions">
                                <button className="secondary-btn" onClick={fetchSettings} disabled={isLoading}>Refresh</button>
                                <button className="primary-btn" onClick={handleSave} disabled={isSaving || isLoading}>
                                    {isSaving ? 'Saving…' : 'Save Config'}
                                </button>
                            </div>
                        </div>

                        <div className="gateway-layout">
                            <div className="gateway-status-card">
                                <div className="status-row">
                                    <div>
                                        <p className="eyebrow">Current Gateway</p>
                                        <h3>PayU India</h3>
                                    </div>
                                    <span className={`gateway-chip ${gatewayActive ? 'online' : 'offline'}`}>
                                        {gatewayActive ? 'Active' : 'Not Configured'}
                                    </span>
                                </div>
                                <div className="meta-grid">
                                    <div>
                                        <label>Merchant ID</label>
                                        <p>{payuConfig.merchantId || '—'}</p>
                                    </div>
                                    <div>
                                        <label>Environment</label>
                                        <p className="env-pill">{payuConfig.env === 'live' ? 'Live' : 'Test/Sandbox'}</p>
                                    </div>
                                    <div>
                                        <label>Endpoint</label>
                                        <p className="small-mono">{payuConfig.endpoint || 'https://secure.payu.in/_payment'}</p>
                                    </div>
                                </div>
                                <div className="status-actions">
                                    <button className="secondary-btn" onClick={handleTestConnection}>Test Connection</button>
                                    {lastTest && (
                                        <span className={`test-chip ${lastTest.status}`}>
                                            {lastTest.status === 'success' ? '✓' : '✕'} {lastTest.message} ({lastTest.at})
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="gateway-form">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>PayU Key</label>
                                        <input
                                            className="admin-form-input"
                                            placeholder="e.g. qwerty123"
                                            value={payuConfig.key}
                                            onChange={e => setPayuConfig({ ...payuConfig, key: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>PayU Salt</label>
                                        <input
                                            className="admin-form-input"
                                            placeholder="e.g. 9djd993kks"
                                            value={payuConfig.salt}
                                            onChange={e => setPayuConfig({ ...payuConfig, salt: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Merchant ID</label>
                                        <input
                                            className="admin-form-input"
                                            placeholder="Your PayU merchant ID"
                                            value={payuConfig.merchantId}
                                            onChange={e => setPayuConfig({ ...payuConfig, merchantId: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Environment</label>
                                        <select
                                            className="admin-form-select"
                                            value={payuConfig.env}
                                            onChange={e => setPayuConfig({ ...payuConfig, env: e.target.value })}
                                        >
                                            <option value="test">Test / Sandbox</option>
                                            <option value="live">Live</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Endpoint URL</label>
                                        <input
                                            className="admin-form-input"
                                            value={payuConfig.endpoint}
                                            onChange={e => setPayuConfig({ ...payuConfig, endpoint: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <p className="muted">Values are stored securely in the `system_settings` table via admin API.</p>
                            </div>
                        </div>
                    </>
                )}

                {(isSuperAdmin || hasPermission(['payments', 'payments.view', 'payments.manage'])) && (
                    <div className="transaction-history">
                        <div className="transaction-header">
                            <h3>Recent Payment Activity</h3>
                            <span className="muted">Synthetic view — connect to gateway logs to replace.</span>
                        </div>
                        <table className="super-table">
                            <thead>
                                <tr>
                                    <th>Txn ID</th>
                                    <th>User</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Method</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {['TXN24123', 'TXN24112', 'TXN24098'].map((id, idx) => (
                                    <tr key={id}>
                                        <td>{id}</td>
                                        <td>Demo User #{idx + 1}</td>
                                        <td>₹{(idx + 1) * 1200}</td>
                                        <td><span className={`status-badge status-${idx === 2 ? 'danger' : 'success'}`}>{idx === 2 ? 'Failed' : 'Success'}</span></td>
                                        <td>{idx === 2 ? 'UPI' : 'Card'}</td>
                                        <td>{new Date().toLocaleTimeString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export function KycManagementTab({ adminSession }) {
    const [kycs, setKycs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState({ pending: 0, verified: 0, rejected: 0, total: 0 });
    const [showSettings, setShowSettings] = useState(false);
    const [selectedKyc, setSelectedKyc] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        fetchKycRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchKycRequests = async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/kyc/requests`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) {
                const requests = data.data || [];
                setKycs(requests);

                // Calculate stats
                const s = {
                    pending: requests.filter(k => k.kyc_status === 'in_progress').length,
                    verified: requests.filter(k => k.kyc_status === 'completed').length,
                    rejected: requests.filter(k => k.kyc_status === 'rejected').length,
                    total: requests.length
                };
                setStats(s);
            }
        } catch (e) {
            console.error('Error fetching KYC requests:', e);
        }
        setIsLoading(false);
    };

    const handleViewDetails = (kyc) => {
        // Parse kyc_data if it's a string
        let parsedData = kyc.kyc_data;
        if (typeof parsedData === 'string') {
            try { parsedData = JSON.parse(parsedData); } catch (e) { }
        }
        setSelectedKyc({
            ...kyc,
            details: parsedData
        });
        setShowDetails(true);
    };

    const updateStatus = async (userId, status) => {
        if (!window.confirm(`Are you sure you want to mark this KYC as ${status}?`)) return;
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/kyc/users/${userId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify({ status })
            });
            const data = await res.json();
            if (data.success) {
                fetchKycRequests();
            } else {
                alert(data.message || 'Failed to update status');
            }
        } catch (e) {
            alert('Server error while updating status');
        }
    };

    const handleSync = async (userId) => {
        try {
            // This calls the CLIENT backend via Admin (assuming cross-service if possible)
            // Or simple local sync if they share DB. Our Admin Backend handles sync via fetchAndSave if implemented.
            // For now, let's assume we want to refresh from DB
            fetchKycRequests();
            alert('Synced with database');
        } catch (e) {
            alert('Sync failed');
        }
    }

    return (
        <div className="kyc-management-tab">
            <div className="super-card">
                <div className="super-card-header">
                    <div className="header-title-group">
                        <h2>🆔 KYC Verification Center</h2>
                        <p className="tab-subtitle">Manage client identity verification through Digio integration</p>
                    </div>
                    <div className="header-actions">
                        <button className="secondary-btn" onClick={fetchKycRequests} disabled={isLoading}>
                            {isLoading ? '⌛ Refreshing...' : '🔄 Refresh'}
                        </button>
                        <button className="primary-btn" onClick={() => setShowSettings(true)}>⚙️ Digio Settings</button>
                    </div>
                </div>

                <div className="kyc-stats-row">
                    <div className="kyc-stat">
                        <span>In Progress</span>
                        <h3>{stats.pending}</h3>
                    </div>
                    <div className="kyc-stat">
                        <span>Verified</span>
                        <h3 style={{ color: '#27ae60' }}>{stats.verified}</h3>
                    </div>
                    <div className="kyc-stat">
                        <span>Rejected</span>
                        <h3 style={{ color: '#e74c3c' }}>{stats.rejected}</h3>
                    </div>
                    <div className="kyc-stat">
                        <span>Total Requests</span>
                        <h3>{stats.total}</h3>
                    </div>
                </div>

                <div className="super-table-container">
                    <table className="super-table">
                        <thead>
                            <tr>
                                <th>Client Name</th>
                                <th>KID</th>
                                <th>Status</th>
                                <th>Submission Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {kycs.length > 0 ? kycs.map(k => (
                                <tr key={k.id}>
                                    <td>
                                        <div className="client-info">
                                            <strong>{k.name}</strong>
                                            <small style={{ display: 'block', color: '#666' }}>{k.email}</small>
                                        </div>
                                    </td>
                                    <td><code style={{ fontSize: '11px' }}>{k.digio_kid || 'N/A'}</code></td>
                                    <td>
                                        <span className={`status-badge ${k.kyc_status}`}>
                                            {k.kyc_status?.toUpperCase()}
                                        </span>
                                    </td>
                                    <td>{new Date(k.request_date).toLocaleDateString()}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="action-btn view"
                                                title="View Verification Details"
                                                onClick={() => handleViewDetails(k)}
                                            >👁️ Details</button>

                                            {k.kyc_status !== 'completed' && (
                                                <button
                                                    className="action-btn confirm"
                                                    title="Approve Verification"
                                                    onClick={() => updateStatus(k.id, 'completed')}
                                                >✅</button>
                                            )}

                                            {k.kyc_status !== 'rejected' && (
                                                <button
                                                    className="action-btn delete"
                                                    title="Reject Verification"
                                                    onClick={() => updateStatus(k.id, 'rejected')}
                                                >❌</button>
                                            )}

                                            <button className="action-btn edit" title="Sync from Digio" onClick={() => handleSync(k.id)}>🔄</button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                                        {isLoading ? 'Loading requests...' : 'No KYC requests found.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <DigioSettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                onSave={(config) => fetchKycRequests()}
                adminSession={adminSession}
            />

            <KycDetailsModal
                isOpen={showDetails}
                onClose={() => setShowDetails(false)}
                kycData={selectedKyc}
            />
        </div>
    );
}

export function LoansTab({ adminSession, initialActiveView = 'loans' }) {
    const NACH_ERROR_MAP = {
        '000': 'Success (Aadhaar number matches with Aadhaar linked with debtor account number validation passed)',
        'AP01': 'Account blocked',
        'AP02': 'Account closed',
        'AP03': 'Account frozen',
        'AP04': 'Account inoperative',
        'AP05': 'No such account',
        'AP06': 'Not a CBS account number or old account number; represent with CBS number',
        'AP07': 'Refer to branch; KYC not completed',
        'AP08': 'Account holder name mismatch with CBS',
        'AP09': 'Account type in mandate is different from CBS',
        'AP10': 'Amount exceeds e-mandate limit',
        'AP11': 'Authentication failed',
        'AP12': 'EMI amount more than limit allowed for the account',
        'AP13': 'Invalid monthly EMI amount; full loan amount mentioned',
        'AP14': 'Invalid user credentials',
        'AP15': 'Mandate not registered: not maintaining required balance',
        'AP16': 'Mandate not registered: minor account',
        'AP17': 'Mandate not registered: NRE account',
        'AP18': 'Mandate registration not allowed for CC account',
        'AP19': 'Mandate registration not allowed for PF account',
        'AP20': 'Mandate registration not allowed for PPF account',
        'AP21': 'Payment stopped by attachment order',
        'AP22': 'Payment stopped by court order',
        'AP23': 'Rejected as per customer confirmation',
        'AP24': 'Withdrawal stopped owing to death of account holder',
        'AP25': 'Withdrawal stopped owing to insolvency of account holder',
        'AP26': 'Withdrawal stopped owing to lunacy of account holder',
        'AP27': 'Invalid frequency',
        'AP28': 'Generic mandate registration failed reason',
        'AP29': 'Technical errors or connectivity issues at backend',
        'AP30': 'Browser closed by customer in mid-transaction',
        'AP31': 'Mandate registration not allowed for joint account',
        'AP32': 'Mandate registration not allowed for wallet account',
        'AP33': 'User rejected the transaction on pre-login page',
        'AP34': 'Account number not registered with Netbanking facility',
        'AP35': 'Debit card validation failed due to invalid card number',
        'AP36': 'Debit card validation failed due to invalid expiry',
        'AP37': 'Debit card validation failed due to invalid PIN',
        'AP38': 'Debit card validation failed due to invalid CVV',
        'AP39': 'OTP invalid',
        'AP40': 'Maximum tries exceeded for OTP',
        'AP41': 'Time expired for OTP',
        'AP42': 'Debit card not activated',
        'AP43': 'Debit card blocked',
        'AP44': 'Debit card hotlisted',
        'AP45': 'Debit card expired',
        'AP46': 'No response received from customer while performing',
        'AP47': 'Account number registered for view-only rights in Netbanking',
        'AP48': 'Aadhaar number does not match with debtor account number',
        'AP51': 'Aadhaar number not linked with the debtor account number',
        '151': 'Merchant XMLNS name empty or incorrect',
        '152': 'Merchant MsgId empty or incorrect',
        '153': 'Merchant CreDtTm empty or incorrect',
        '154': 'Merchant ReqInitPty Id empty or incorrect',
        '155': 'Merchant CatCode empty or incorrect',
        '156': 'Merchant UtilCode empty or incorrect',
        '157': 'Merchant CatDesc empty or incorrect',
        '158': 'Merchant ReqInitPty name empty or incorrect',
        '159': 'Merchant MndtReqId empty or incorrect',
        '160': 'Merchant SeqTp empty or incorrect',
        '161': 'Merchant Frqcy empty or incorrect',
        '162': 'Merchant FrstColltnDt empty or incorrect',
        '163': 'Merchant FnlColltnDt empty or incorrect',
        '164': 'Merchant ColltnAmt ccy type empty or incorrect',
        '165': 'Merchant ColltnAmt empty or incorrect',
        '166': 'Merchant MaxAmt ccy type empty or incorrect',
        '167': 'Merchant MaxAmt empty or incorrect',
        '168': 'Merchant Creditor name empty or incorrect',
        '169': 'Merchant Creditor Acc No empty or incorrect',
        '170': 'Merchant Creditor MmbId empty or incorrect',
        '171': 'Merchant MandateReqId incorrect',
        '172': 'Merchant Creditor Acc No empty',
        '173': 'Merchant Info not available',
        '174': 'Merchant ReqInitPty not available',
        '175': 'Merchant Creditor Acc Details not available',
        '176': 'Merchant GrpHdr not available',
        '177': 'Merchant Mndt not available',
        '178': 'Merchant MndtAuthReq empty or not available',
        '179': 'Merchant CheckSum validation failed',
        '180': 'Merchant Signature validation failed',
        '181': 'Error in decrypting Creditor Acc No',
        '182': 'Error in decrypting FrstColltnDt',
        '183': 'Error in decrypting FnlColltnDt',
        '184': 'Error in decrypting ColltnAmt',
        '185': 'Error in decrypting MaxAmt',
        '186': 'Merchant invalid request',
        '187': 'Merchant Id empty or incorrect',
        '188': 'Merchant ManReqDoc incorrect',
        '189': 'Merchant CheckSum empty or not available',
        '190': 'Merchant Signature not found',
        '191': 'Merchant GrpHdr missing some tag',
        '192': 'Merchant ReqInitPty missing some tag',
        '193': 'Merchant Mndt missing some tag',
        '194': 'Merchant CrAccDtl missing some tag',
        '195': 'Merchant Certificate not found',
        '196': 'Merchant Signature algorithm incorrect',
        '197': 'Merchant Signature Digest algorithm incorrect',
        '198': 'Merchant first date is after the final date',
        '199': 'Merchant CrAccDtl not available',
        '200': 'Merchant first date not available',
        '201': 'Merchant final date not available',
        '202': 'Merchant first date empty',
        '203': 'Merchant final date empty',
        '204': 'Merchant ManReqDoc empty not available',
        '205': 'MerchantId not in approved list',
        '206': 'Merchant ColltnAmt and MaxAmt empty',
        '207': 'Merchant ColltnAmt and MaxAmt exist',
        '208': 'Merchant Catcode not in approved list',
        '209': 'Merchant MsgId is duplicate',
        '210': 'Merchant Frequency type is invalid',
        '211': 'Merchant Sequence type is invalid',
        '212': 'Merchant Cat Description is not approved list',
        '213': 'Merchant UtilCode is not in approved list',
        '214': 'Merchant Req Pay ID not in approved list',
        '215': 'Merchant Creditor Acc Details name not in approved list',
        '216': 'Merchant Occurences is empty',
        '217': 'Merchant Debitor name empty or incorrect',
        '218': 'Merchant Debitor Account number empty or incorrect',
        '219': 'Merchant Debitor is missing some tags',
        '220': 'Merchant Debitor Acc No empty',
        '221': 'Merchant Debitor Acc not available',
        '222': 'Merchant Creditor and Debtor account number is same',
        '223': 'Merchant info id, util and creditor account number are not equals',
        '234': 'Merchant Creditor account number is not in approved list',
        '235': 'Merchant Mandate ID is duplicate',
        '236': 'Merchant Debitor Account number decrypt error',
        '237': 'Merchant decrypt error',
        '238': 'Merchant Bank id not available',
        '239': 'Merchant Bank id not in approved list',
        '240': 'Merchant Bank debit flag error',
        '251': 'Bank request is invalid',
        '252': 'Bank XMLNS is empty or incorrect',
        '253': 'Bank response type is not available or empty',
        '254': 'Bank checksum is not available or empty',
        '255': 'Bank mandate request document is incorrect',
        '256': 'Bank id not available or empty',
        '257': 'Error in decrypting accepted value',
        '258': 'Error in decrypting accepted ref number',
        '259': 'Error in decrypting reason code',
        '260': 'Error in decrypting reason description',
        '261': 'Error in decrypting rejected by',
        '262': 'Bank NPCI ref id empty or incorrect',
        '263': 'Error code not available in error XML',
        '264': 'Error description not available in error XML',
        '265': 'Rejected by not available in error XML',
        '266': 'Mandate error response not available in error XML',
        '267': 'Checksum validation failed',
        '268': 'Bank UndrlygAccptncDtls not available',
        '269': 'Bank GrpHdr empty or not available',
        '270': 'Bank MsgId empty or incorrect',
        '271': 'Bank CreDtTm empty or incorrect',
        '272': 'Bank ReqInitPty empty or incorrect',
        '273': 'Bank OrgnlMsgInf not available',
        '274': 'Bank MndtReqId empty or incorrect',
        '275': 'Bank UndrlygAccptncDtls CreDtTm empty or incorrect',
        '276': 'Bank Accptd empty',
        '277': 'Bank AccptRefNo empty or incorrect',
        '278': 'Bank RjctRsn not available',
        '279': 'Bank RjctRsn ReasonCode not empty',
        '280': 'Bank RjctRsn ReasonDesc not empty',
        '281': 'Bank RjctRsn RejectBy not empty',
        '282': 'Bank RjctRsn ReasonCode empty or incorrect',
        '283': 'Bank RjctRsn ReasonDesc empty or incorrect',
        '284': 'Bank RjctRsn RejectBy empty or incorrect',
        '285': 'Bank Certificate not found',
        '286': 'Bank IFSC code incorrect',
        '287': 'Bank RespType is incorrect',
        '288': 'Bank GrpHdr missing some tags',
        '289': 'Bank UndrlygAccptncDtls missing some tags',
        '290': 'Bank OrgnlMsgInf missing some tags',
        '291': 'Bank IFSC tag is missing',
        '292': 'Bank DBTR not available',
        '293': 'Bank AccptncRslt not available',
        '294': 'Bank RjctRsn missing some tags',
        '295': 'Bank ManReqDoc not available or empty',
        '296': 'Bank Accptd type incorrect',
        '297': 'Bank Signature not available',
        '298': 'Bank Signature Digest algorithm incorrect',
        '299': 'Bank Signature validation failed',
        '300': 'Bank Signature algorithm incorrect',
        '301': 'BankId not in approved list',
        '302': 'Bank MsgId is duplicate',
        '303': 'Bank Accepted Ref number is duplicate',
        '305': 'Bank response time out',
        '306': 'Bank mandate id is duplicate',
        '307': 'Bank NPCI ref id not valid',
        '308': 'Bank reason code and description not in approved list',
        '470': 'Bank Mndt ReqId not same as NPCI request',
        '471': 'Bank NPCI RefId not same as NPCI request',
        '472': 'Invalid authmode received',
        '473': 'Bank invalid id',
        '474': 'Bank returning error XML',
        '475': 'Invalid JSON structure',
        '476': 'Sponsor bank not in approved list for corporate',
        '477': 'Sponsor bank not certified',
        '478': 'Destination bank not certified',
        '479': 'Bank invalid XML structure',
        '480': 'Sponsor/destination bank not certified',
        '481': 'Destination/sponsor bank not certified',
        '486': 'No variant is eligible for selection',
        '487': 'No variant is eligible for selection',
        '490': 'Bank ACCT_TYPE tag is invalid',
        '491': 'Bank ACCT_TYPE tag is null',
        '492': 'Current process is invalid',
        '493': 'Merchant Req Frqncy validation error',
        '494': 'Invalid Frqncy tag value',
        '495': 'First collection date invalid',
        '496': 'Final collection date invalid',
        '497': 'Sponsor bank not certified for the variant',
        '498': 'Destination bank not certified for the variant',
        '508': 'Merchant creditor phone number empty or incorrect',
        '509': 'Merchant creditor mobile number empty or incorrect',
        '510': 'Merchant creditor email ID empty or incorrect',
        '511': 'Merchant creditor PAN number empty or incorrect',
        '512': 'Merchant creditor phone number encryption error',
        '513': 'Merchant creditor mobile number encryption error',
        '514': 'Merchant creditor email Id encryption error',
        '515': 'Merchant creditor PAN number encryption error',
        '516': 'Merchant creditor phone number is invalid',
        '517': 'Merchant creditor mobile number is invalid',
        '518': 'Merchant creditor account type is invalid',
        '519': 'Merchant creditor email Id is invalid',
        '520': 'Merchant creditor PAN number is invalid',
        '521': 'Merchant creditor scheme name is invalid',
        '522': 'Merchant creditor consumer reference number is invalid',
        '523': 'Merchant creditor mandate type is invalid',
        '524': 'Merchant creditor co-operate sponsor bank is invalid',
        '525': 'Error in decrypting phone number',
        '526': 'Error in decrypting mobile number',
        '527': 'Error in decrypting email Id',
        '528': 'Error in decrypting PAN number',
        '529': 'SpID missing for the merchant',
        '530': 'Merchant SpId combination not in DB',
        '531': 'Authmode and BankId are mandatory',
        '532': 'Email max length exceeded',
        '534': 'Mandate verify details response is null',
        '536': 'Error in posting mandate details',
        '537': 'Error in processing mandate verify response',
        '539': 'Mandate verify details response is null',
        '542': 'Error in posting OTP details',
        '543': 'OTP verification API URL null',
        '601': 'Invalid debit card number',
        '602': 'Invalid expiry/validity',
        '603': 'Invalid CVV',
        '604': 'Account details do not match',
        '605': 'OTP verification failure',
        '607': 'Previous request in progress',
        '608': 'Bank restricts duplicate request',
        'M003': 'Drawer signature differs',
        'M004': 'Drawer signature required',
        'M005': 'Drawer signature to operate account not received',
        'M006': 'Drawer authority to operate account not received',
        'M007': 'Alterations require drawer authentication',
        'M008': 'Company for stamp required or wrong',
        'M009': 'Mandate in old format',
        'M011': 'Payment stopped by attachment order',
        'M012': 'Payment stopped by court order',
        'M013': 'Withdrawal stopped owing to death of account holder',
        'M014': 'Withdrawal stopped owing to lunacy of account holder',
        'M015': 'Withdrawal stopped owing to insolvency of account holder',
        'M021': 'Duplicate mandate; first presented mandate already',
        'M022': 'Mandate presented in ACH as well as ECS',
        'M023': 'Refer to branch; KYC not completed',
        'M024': 'Amount in words and figure differ',
        'M025': 'Present under improper management category',
        'M026': 'Account frozen',
        'M027': 'Image not clear',
        'M030': 'Mandate registration not allowed for CC account',
        'M031': 'Not a CBS account or old account number; represent with CBS account number',
        'M032': 'Rejected as per customer confirmation',
        'M033': 'Invalid monthly EMI amount; full loan amount mentioned',
        'M034': 'EMI amount more than limit allowed for the account',
        'M035': 'Corporate name mismatch',
        'M037': 'Account closed',
        'M038': 'No such account',
        'M041': 'Account blocked',
        'M049': 'Drawer signature not updated in Bank CBS',
        'M050': 'Drawer signature illegible in mandate form',
        'M051': 'Mandate not registered: NRE account',
        'M052': 'Mandate not registered: minor account',
        'M053': 'Mandate registration not allowed for PF account',
        'M054': 'Mandate registration not allowed for PPF account',
        'M055': 'Account inoperative',
        'M056': 'Mandate not registered: not maintaining required balance',
        'M057': 'Account holder name mismatch with CBS',
        'M060': 'Invalid frequency',
        'M066': 'Joint signature required',
        'M067': 'Thumb print in CBS but customer signature in mandate or vice versa',
        'M068': 'Account type in mandate is different from CBS',
        'M074': 'Data mismatch with image - account number',
        'M075': 'Data mismatch with image - account type',
        'M077': 'Data mismatch with image - frequency',
        'M078': 'Data mismatch with image - period',
        'M079': 'Data mismatch with image - debit type',
        'M080': 'Data mismatch with image - amount',
        'M081': 'Data mismatch with image - start date',
        'M082': 'Data mismatch with image - end date',
        'M083': 'Data mismatch with image - payer name',
        'M084': 'Data mismatch with image - debtor bank name',
        'M085': 'Data mismatch with image - more than one field',
        'M088': 'API data mismatch with customer info and data mandate',
        'M089': 'Aadhaar number mismatch in X509 certificate and mandate',
        'M090': 'Aadhaar number mismatch in X509 certificate and Bank CBS',
        'M091': 'eSign signature is tampered or corrupt',
        'M092': 'Signed content does not tally with data mandate',
        'M093': 'Aadhaar not mapped to account number'
    };
    const NACH_PRESENTATION_ERROR_MAP = {
        '01': 'Account closed',
        '02': 'No such account',
        '04': 'Balance insufficient',
        '05': 'Not arranged for',
        '06': 'Payment stopped by drawer',
        '07': 'Payment stopped under court order/account under litigation',
        '08': 'Mandate not received',
        '11': 'Invalid IFSC/MICR code',
        '12': 'Mismatch in mandate frequency',
        '13': 'Duplicate transaction already debited under ACH/NACH',
        '14': 'Mandate expired',
        '15': 'Incorrect amount; mismatch between mandate and transaction',
        '16': 'Customer name mismatch',
        '17': 'Returned as per customer request',
        '21': 'Invalid UMRN or inactive mandate',
        '22': 'Mandate not valid for debit transaction',
        '23': 'Mismatch in mandate debtor account number',
        '24': 'Mismatch in mandate debtor bank',
        '25': 'Mismatch in mandate currency',
        '26': 'Amount exceeds mandate max amount',
        '27': 'Mandate amount mismatch',
        '28': 'Date before mandate start date',
        '29': 'Date after mandate end date',
        '30': 'Mandate user number mismatch',
        '31': 'Duplicate reference number',
        '32': 'Invalid date',
        '33': 'Item unwound',
        '34': 'Invalid amount',
        '51': 'KYC documents pending',
        '52': 'Documents pending for account holder turning major',
        '53': 'Account inoperative',
        '54': 'Dormant account',
        '55': 'Zero balance/no transactions; first transaction must be cash/self cheque',
        '56': 'Small account; first transaction to be from base branch',
        '57': 'Amount exceeds bank per-transaction limit',
        '58': 'Account reached maximum debit limit set by bank',
        '59': 'Network failure (CBS)',
        '60': 'Account holder expired',
        '61': 'Mandate cancelled',
        '68': 'Account blocked or frozen',
        '69': 'Customer insolvent/insane',
        '70': 'Customer to refer to the branch',
        '72': 'Item cancelled',
        '73': 'Settlement failed',
        '74': 'Invalid file format',
        '75': 'Transaction cancelled by user',
        '76': 'Invalid Aadhaar format',
        '77': 'Invalid currency',
        '78': 'Invalid bank identifier',
        '79': 'Item sent before SOD or after FC',
        '80': 'Wrong IIN',
        '81': 'Product is missing',
        '82': 'Item marked pending',
        '83': 'Unsupported field',
        '84': 'Invalid data format',
        '85': 'Participant not mapped to the product',
        '86': 'Invalid transaction code',
        '87': 'Missing original transaction',
        '88': 'Invalid original transaction',
        '89': 'Original date mismatch',
        '90': 'Amount does not match with original',
        '91': 'Information does not match with original',
        '92': 'Core error',
        '93': 'Wrong clearing house name in SFG',
        '94': 'Amount is zero',
        '95': 'Inactive Aadhaar',
        '96': 'Aadhaar mapping does not exist/not mapped to IIN',
        '97': 'Bad batch corporate user number/name',
        '98': 'Bad item corporate user number/name',
        '99': 'Too many mark pending returns',
        'DG101': 'Transaction already successful for defined frequency',
        'DG102': 'Transaction limit breached',
        'DG103': 'Transaction not allowed; retry after 3 days from last failure',
        'DG104': 'Debit already created for this UMRN'
    };

    const [loans, setLoans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [loanSearch, setLoanSearch] = useState('');
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [activeLoanView, setActiveLoanView] = useState(initialActiveView);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({});

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [loanToDelete, setLoanToDelete] = useState(null);
    const [isMandateModalOpen, setIsMandateModalOpen] = useState(false);
    const [mandateLoading, setMandateLoading] = useState(false);
    const [mandateMessage, setMandateMessage] = useState('');
    const [mandateDetails, setMandateDetails] = useState(null);
    const [mandateRefreshLoading, setMandateRefreshLoading] = useState(false);
    const [mandateCrnLoading, setMandateCrnLoading] = useState(false);
    const [mandateCancelLoading, setMandateCancelLoading] = useState(false);
    const [debitLoading, setDebitLoading] = useState(false);
    const [debitMessage, setDebitMessage] = useState('');
    const [debitDetails, setDebitDetails] = useState(null);
    const [debitFetchLoading, setDebitFetchLoading] = useState(false);
    const [debitFetchMessage, setDebitFetchMessage] = useState('');
    const [debitActionLoading, setDebitActionLoading] = useState(false);
    const [debitActionMessage, setDebitActionMessage] = useState('');
    const [transactionActionLoading, setTransactionActionLoading] = useState(false);
    const [transactionActionMessage, setTransactionActionMessage] = useState('');
    const [transactionFetchLoading, setTransactionFetchLoading] = useState(false);
    const [transactionFetchMessage, setTransactionFetchMessage] = useState('');
    const [banksLoading, setBanksLoading] = useState(false);
    const [banksMessage, setBanksMessage] = useState('');
    const [banks, setBanks] = useState([]);
    const [bankFilter, setBankFilter] = useState({ mode: 'API', pennyLess: true, search: '' });
    const [mandateForm, setMandateForm] = useState({
        corporate_config_id: '',
        auth_mode: 'API',
        notify_customer: true,
        include_authentication_url: true,
        expire_in_days: 10,
        customer_notification_mode: 'SMS',
        customer_identifier: '',
        customer_name: '',
        customer_ref_number: '',
        scheme_ref_number: '',
        customer_account_number: '',
        customer_account_type: 'SAVINGS',
        destination_bank_id: '',
        destination_bank_name: '',
        management_category: 'L001',
        is_recurring: true,
        frequency: 'Monthly',
        first_collection_date: '',
        final_collection_date: '',
        collection_amount: '',
        maximum_amount: '',
        cancel_reason: 'C001',
        cancel_umrn: '',
        cancel_dest_ifsc: ''
    });

    useEffect(() => {
        setActiveLoanView(initialActiveView);
    }, [initialActiveView]);
    const [debitForm, setDebitForm] = useState({
        umrn: '',
        settlement_date: '',
        corporate_account_number: '',
        settlement_config_profile_id: '',
        amount: '',
        amount_in_paise: '',
        narration: '',
        unique_key: '',
        frequency: 'onetime',
        payment_count: 1,
        plan_id: '',
        reference_id: '',
        action_plan_id: '',
        action_type: 'pause',
        transaction_id: '',
        transaction_action: 'cancel'
    });

    const parseJsonSafe = (value) => {
        if (!value) return null;
        if (typeof value === 'object') return value;
        try { return JSON.parse(value); } catch (e) { return null; }
    };

    const normalizeErrorCode = (value) => {
        if (!value) return null;
        const str = String(value).trim();
        const match = str.match(/(AP\d{2}|M\d{3}|\b\d{3}\b)/);
        return match ? match[1] : null;
    };

    const extractMandateErrorInfo = (mandateData) => {
        let data = parseJsonSafe(mandateData);
        if (Array.isArray(data)) data = data[0];
        if (!data) return null;
        const candidates = [
            data?.npci_auth_failed_error,
            data?.npci_auth_reject_reason,
            data?.res_report?.[0]?.reject_code,
            data?.ack_report?.[0]?.reject_code,
            data?.res_report?.[0]?.reject_reason,
            data?.ack_report?.[0]?.reject_reason
        ];
        for (const candidate of candidates) {
            const code = normalizeErrorCode(candidate);
            if (code && code !== '000') {
                return {
                    code,
                    description: NACH_ERROR_MAP[code],
                    raw: candidate
                };
            }
        }
        return null;
    };

    const extractPresentationErrorInfo = (debitData) => {
        let data = parseJsonSafe(debitData);
        if (!data) return null;
        const candidates = [
            data?.details?.failure_code,
            data?.details?.failure_reason,
            data?.last_transaction?.state,
            data?.details?.status
        ];
        for (const candidate of candidates) {
            if (!candidate) continue;
            const str = String(candidate).trim();
            const match = str.match(/DG\d{3}|\b\d{2}\b/);
            if (match) {
                const code = match[0];
                return {
                    code,
                    description: NACH_PRESENTATION_ERROR_MAP[code],
                    raw: candidate
                };
            }
        }
        return null;
    };

    const hasPermission = (perm) => adminSession?.role === 'super_admin' || adminSession?.permissions?.includes(perm);

    const fetchLoans = useCallback(async () => {
        setIsLoading(true);
        try {
            let url = `${API_CONFIG.BASE_URL}/api/admin/loans?limit=100`;
            if (filterStatus) url += `&status=${filterStatus}`;
            if (loanSearch) url += `&search=${encodeURIComponent(loanSearch)}`;

            const response = await fetch(url, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await response.json();
            if (data.success) {
                setLoans(data.data?.loans || []);
            }
        } catch (error) {
            console.error('Error fetching loans:', error);
        } finally {
            setIsLoading(false);
        }
    }, [adminSession, filterStatus, loanSearch]);

    useEffect(() => {
        fetchLoans();
    }, [fetchLoans]);

    const handleUpdateStatus = async (loanId, newStatus) => {
        if (!window.confirm(`Are you sure you want to mark this loan as ${newStatus}?`)) return;
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${loanId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify({ status: newStatus })
            });
            const data = await response.json();
            if (data.success) {
                alert(`Loan ${newStatus.toLowerCase()} successfully`);
                fetchLoans();
                if (isViewModalOpen) setIsViewModalOpen(false);
            } else {
                alert(data.message || 'Failed to update loan');
            }
        } catch (error) {
            console.error('❌ Error updating loan:', error);
            alert('Error updating loan');
        }
    };

    const openDeleteModal = (loan) => {
        setLoanToDelete(loan);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteLoan = async () => {
        if (!loanToDelete) return;

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${loanToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'x-user-id': adminSession?.userId
                }
            });
            const data = await response.json();
            if (data.success) {
                alert('Loan deleted successfully');
                fetchLoans();
                setIsDeleteModalOpen(false);
                setLoanToDelete(null);
            } else {
                alert(data.message || 'Failed to delete loan');
            }
        } catch (error) {
            console.error('Error deleting loan:', error);
            alert('Error deleting loan');
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify(editFormData)
            });
            const data = await response.json();
            if (data.success) {
                alert('Loan updated successfully');
                setIsEditModalOpen(false);
                fetchLoans();
            } else {
                alert(data.message || 'Failed to update loan');
            }
        } catch (error) {
            console.error('Error updating loan:', error);
            alert('Error updating loan');
        }
    };

    const openViewModal = (loan) => {
        setSelectedLoan(loan);
        setIsViewModalOpen(true);
    };

    const openEditModal = (loan) => {
        setSelectedLoan(loan);
        setEditFormData({
            amount: loan.amount,
            interest_rate: loan.interest_rate,
            tenure_months: loan.tenure_months,
            status: loan.status
        });
        setIsEditModalOpen(true);
    };

    const isMandateDone = (loan) => {
        if (!loan.mandate_id) return false;
        const status = (loan.mandate_status || '').toLowerCase();
        if (!status) return true;
        const badStates = new Set(['rejected', 'failed', 'cancelled', 'revoked', 'expired', 'auth_failed']);
        if (badStates.has(status)) return false;
        return true;
    };

    const openMandateModal = async (loan) => {
        setSelectedLoan(loan);
        setMandateMessage('');
        setMandateDetails(null);
        setDebitMessage('');
        setDebitDetails(null);
        setDebitFetchMessage('');
        setDebitActionMessage('');
        setTransactionActionMessage('');
        setTransactionFetchMessage('');
        setBanksMessage('');
        const today = new Date().toISOString().slice(0, 10);
        const customerName = `${loan.first_name || ''} ${loan.last_name || ''}`.trim();
        setMandateForm(prev => ({
            ...prev,
            customer_identifier: loan.email || loan.phone || prev.customer_identifier,
            customer_name: customerName || loan.email || loan.phone || prev.customer_name,
            customer_ref_number: prev.customer_ref_number || `LOAN${loan.id}`,
            first_collection_date: prev.first_collection_date || today,
            collection_amount: prev.collection_amount || loan.monthly_emi || loan.amount || ''
        }));
        const parsedMandate = parseJsonSafe(loan.mandate_data);
        const umrn =
            parsedMandate?.mandate_details?.umrn ||
            parsedMandate?.sub_user?.umrn ||
            parsedMandate?.umrn ||
            '';
        const debitPlanId =
            parsedMandate?.debit_schedule?.id ||
            parsedMandate?.debit_details?.id ||
            '';
        const lastTxnId =
            parsedMandate?.debit_details?.last_transaction?.id ||
            parsedMandate?.debit_schedule?.last_transaction?.id ||
            '';
        setDebitForm(prev => ({
            ...prev,
            umrn: umrn || prev.umrn,
            settlement_date: prev.settlement_date || today,
            amount: prev.amount || loan.monthly_emi || loan.amount || '',
            unique_key: prev.unique_key || `umrn_${loan.id}_${Date.now()}`.slice(0, 30),
            plan_id: debitPlanId || prev.plan_id,
            reference_id: prev.reference_id || prev.unique_key || '',
            action_plan_id: debitPlanId || prev.action_plan_id,
            transaction_id: lastTxnId || prev.transaction_id
        }));
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/settings`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) {
                const s = data.data || {};
                setMandateForm(prev => ({
                    ...prev,
                    corporate_config_id: s['digio.mandate.corporate_config_id'] || prev.corporate_config_id,
                    auth_mode: s['digio.mandate.auth_mode'] || prev.auth_mode,
                    notify_customer: s['digio.mandate.notify_customer'] === 'true' ? true : prev.notify_customer,
                    include_authentication_url: s['digio.mandate.include_authentication_url'] === 'true' ? true : prev.include_authentication_url,
                    expire_in_days: s['digio.mandate.expire_in_days'] ? Number(s['digio.mandate.expire_in_days']) : prev.expire_in_days,
                    customer_notification_mode: s['digio.mandate.customer_notification_mode'] || prev.customer_notification_mode
                }));
            }
        } catch (e) {
            console.error('Failed to load mandate settings', e);
        }
        setIsMandateModalOpen(true);
    };

    const handleLoadBanks = async () => {
        setBanksLoading(true);
        setBanksMessage('');
        try {
            const params = new URLSearchParams();
            if (bankFilter.mode) params.append('mode', bankFilter.mode);
            if (bankFilter.pennyLess) params.append('penny_less', 'true');
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/mandate/banks?${params.toString()}`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) {
                setBanks(Array.isArray(data.data) ? data.data : []);
                setBanksMessage('Banks loaded.');
            } else {
                setBanksMessage(data.message || 'Failed to load banks');
            }
        } catch (e) {
            console.error('Banks load error:', e);
            setBanksMessage('Server error while loading banks');
        } finally {
            setBanksLoading(false);
        }
    };

    const handleFetchDebitDetails = async () => {
        if (!selectedLoan) return;
        if (!debitForm.plan_id && !debitForm.reference_id) {
            setDebitFetchMessage('Plan ID or Reference ID is required.');
            return;
        }
        setDebitFetchLoading(true);
        setDebitFetchMessage('');
        try {
            const url = debitForm.plan_id
                ? `${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/debit/${encodeURIComponent(debitForm.plan_id)}`
                : `${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/debit/by-reference/${encodeURIComponent(debitForm.reference_id)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await response.json();
            if (data.success) {
                setDebitDetails(data.data);
                setDebitFetchMessage('Debit details fetched.');
                fetchLoans();
            } else {
                setDebitFetchMessage(data.message || 'Failed to fetch debit details');
            }
        } catch (error) {
            console.error('Debit fetch error:', error);
            setDebitFetchMessage('Server error while fetching debit details');
        } finally {
            setDebitFetchLoading(false);
        }
    };

    const handleDebitPlanAction = async () => {
        if (!selectedLoan) return;
        if (!debitForm.action_plan_id) {
            setDebitActionMessage('Scheduled plan ID is required.');
            return;
        }
        if (!debitForm.action_type) {
            setDebitActionMessage('Action type is required.');
            return;
        }
        setDebitActionLoading(true);
        setDebitActionMessage('');
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/debit/action`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify({
                    scheduled_plan_id: debitForm.action_plan_id,
                    action: debitForm.action_type
                })
            });
            const data = await response.json();
            if (data.success) {
                setDebitDetails(data.data);
                setDebitActionMessage('Action submitted successfully.');
                fetchLoans();
            } else {
                setDebitActionMessage(data.message || 'Failed to perform action');
            }
        } catch (error) {
            console.error('Debit action error:', error);
            setDebitActionMessage('Server error while performing action');
        } finally {
            setDebitActionLoading(false);
        }
    };

    const handleTransactionAction = async () => {
        if (!selectedLoan) return;
        if (!debitForm.transaction_id) {
            setTransactionActionMessage('Transaction ID is required.');
            return;
        }
        if (!debitForm.transaction_action) {
            setTransactionActionMessage('Action is required.');
            return;
        }
        setTransactionActionLoading(true);
        setTransactionActionMessage('');
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/debit/transaction/action`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify({
                    transaction_id: debitForm.transaction_id,
                    action: debitForm.transaction_action
                })
            });
            const data = await response.json();
            if (data.success) {
                setDebitDetails(data.data);
                setTransactionActionMessage('Transaction action submitted.');
                fetchLoans();
            } else {
                setTransactionActionMessage(data.message || 'Failed to action transaction');
            }
        } catch (error) {
            console.error('Transaction action error:', error);
            setTransactionActionMessage('Server error while acting on transaction');
        } finally {
            setTransactionActionLoading(false);
        }
    };

    const handleFetchTransactionDetails = async () => {
        if (!selectedLoan) return;
        if (!debitForm.transaction_id) {
            setTransactionFetchMessage('Transaction ID is required.');
            return;
        }
        setTransactionFetchLoading(true);
        setTransactionFetchMessage('');
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/debit/transaction/${encodeURIComponent(debitForm.transaction_id)}`, {
                method: 'GET',
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await response.json();
            if (data.success) {
                setDebitDetails(data.data);
                setTransactionFetchMessage('Transaction details fetched.');
                fetchLoans();
            } else {
                setTransactionFetchMessage(data.message || 'Failed to fetch transaction details');
            }
        } catch (error) {
            console.error('Transaction fetch error:', error);
            setTransactionFetchMessage('Server error while fetching transaction details');
        } finally {
            setTransactionFetchLoading(false);
        }
    };

    const handleCreateDebit = async () => {
        if (!selectedLoan) return;
        if (!debitForm.umrn) {
            setDebitMessage('UMRN is required.');
            return;
        }
        if (!debitForm.corporate_account_number && !debitForm.settlement_config_profile_id) {
            setDebitMessage('Corporate account number or settlement profile ID is required.');
            return;
        }
        if (!debitForm.amount && !debitForm.amount_in_paise) {
            setDebitMessage('Amount or amount in paise is required.');
            return;
        }

        setDebitLoading(true);
        setDebitMessage('');
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/debit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify({
                    umrn: debitForm.umrn,
                    settlement_date: debitForm.settlement_date || undefined,
                    corporate_account_number: debitForm.corporate_account_number || undefined,
                    settlement_config_profile_id: debitForm.settlement_config_profile_id || undefined,
                    amount: debitForm.amount || undefined,
                    amount_in_paise: debitForm.amount_in_paise || undefined,
                    narration: debitForm.narration || undefined,
                    unique_key: debitForm.unique_key || undefined,
                    frequency: debitForm.frequency || undefined,
                    payment_count: debitForm.payment_count || undefined
                })
            });
            const data = await response.json();
            if (data.success) {
                setDebitDetails(data.data);
                setDebitMessage('Debit schedule created successfully.');
                fetchLoans();
            } else {
                setDebitMessage(data.message || 'Failed to create debit schedule');
            }
        } catch (error) {
            console.error('Debit schedule error:', error);
            setDebitMessage('Server error while creating debit schedule');
        } finally {
            setDebitLoading(false);
        }
    };

    const handleCancelMandate = async () => {
        if (!selectedLoan) return;
        if (!selectedLoan.mandate_id && !mandateForm.cancel_umrn) {
            setMandateMessage('Mandate ID or UMRN is required to cancel.');
            return;
        }
        if (!mandateForm.cancel_reason) {
            setMandateMessage('Cancel reason is required.');
            return;
        }
        setMandateCancelLoading(true);
        setMandateMessage('');
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify({
                    digio_mandate_id: selectedLoan.mandate_id || undefined,
                    umrn: mandateForm.cancel_umrn || undefined,
                    reason: mandateForm.cancel_reason,
                    dest_ifsc: mandateForm.cancel_dest_ifsc || undefined
                })
            });
            const data = await response.json();
            if (data.success) {
                setMandateMessage('Mandate cancellation requested.');
                setMandateDetails(data.data);
                fetchLoans();
            } else {
                setMandateMessage(data.message || 'Failed to cancel mandate');
            }
        } catch (error) {
            console.error('Mandate cancel error:', error);
            setMandateMessage('Server error while cancelling mandate');
        } finally {
            setMandateCancelLoading(false);
        }
    };

    const handleFetchMandateDetails = async () => {
        if (!selectedLoan?.mandate_id) {
            setMandateMessage('No mandate ID found for this loan.');
            return;
        }
        setMandateRefreshLoading(true);
        setMandateMessage('');
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate`, {
                method: 'GET',
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await response.json();
            if (data.success) {
                setMandateDetails(data.data);
                setMandateMessage('Mandate details refreshed.');
                fetchLoans();
            } else {
                setMandateMessage(data.message || 'Failed to fetch mandate details');
            }
        } catch (error) {
            console.error('Mandate fetch error:', error);
            setMandateMessage('Server error while fetching mandate details');
        } finally {
            setMandateRefreshLoading(false);
        }
    };

    const handleFetchMandateByCrn = async () => {
        if (!mandateForm.customer_ref_number) {
            setMandateMessage('Customer Ref Number (CRN) is required.');
            return;
        }
        setMandateCrnLoading(true);
        setMandateMessage('');
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/by-crn/${encodeURIComponent(mandateForm.customer_ref_number)}`, {
                method: 'GET',
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await response.json();
            if (data.success) {
                setMandateDetails(data.data);
                setMandateMessage('Mandate details fetched by CRN.');
                fetchLoans();
            } else {
                setMandateMessage(data.message || 'Failed to fetch mandate details by CRN');
            }
        } catch (error) {
            console.error('Mandate fetch by CRN error:', error);
            setMandateMessage('Server error while fetching by CRN');
        } finally {
            setMandateCrnLoading(false);
        }
    };

    const handleCreateMandate = async (e) => {
        e.preventDefault();
        if (!selectedLoan) return;
        if (!mandateForm.corporate_config_id) {
            setMandateMessage('Corporate Config ID is required.');
            return;
        }
        if (!mandateForm.customer_account_number) {
            setMandateMessage('Customer account number is required.');
            return;
        }
        if (!mandateForm.customer_ref_number) {
            setMandateMessage('Customer reference number is required.');
            return;
        }
        if (!mandateForm.first_collection_date) {
            setMandateMessage('First collection date is required.');
            return;
        }
        if (!mandateForm.collection_amount && !mandateForm.maximum_amount) {
            setMandateMessage('Collection amount or maximum amount is required.');
            return;
        }
        if (mandateForm.is_recurring && !mandateForm.frequency) {
            setMandateMessage('Frequency is required for recurring mandate.');
            return;
        }

        setMandateLoading(true);
        setMandateMessage('');
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/loans/${selectedLoan.id}/mandate/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminSession?.userId
                },
                body: JSON.stringify({
                    corporate_config_id: mandateForm.corporate_config_id,
                    auth_mode: mandateForm.auth_mode,
                    notify_customer: mandateForm.notify_customer,
                    include_authentication_url: mandateForm.include_authentication_url,
                    expire_in_days: mandateForm.expire_in_days,
                    customer_notification_mode: mandateForm.customer_notification_mode,
                    customer_identifier: mandateForm.customer_identifier,
                    mandate_data: {
                        management_category: mandateForm.management_category,
                        is_recurring: mandateForm.is_recurring,
                        frequency: mandateForm.is_recurring ? mandateForm.frequency : undefined,
                        first_collection_date: mandateForm.first_collection_date,
                        final_collection_date: mandateForm.final_collection_date || undefined,
                        collection_amount: mandateForm.collection_amount ? Number(mandateForm.collection_amount) : undefined,
                        maximum_amount: mandateForm.maximum_amount ? Number(mandateForm.maximum_amount) : undefined,
                        customer_name: mandateForm.customer_name,
                        customer_ref_number: mandateForm.customer_ref_number,
                        scheme_ref_number: mandateForm.scheme_ref_number || undefined,
                        customer_mobile: selectedLoan.phone,
                        customer_email: selectedLoan.email,
                        customer_account_number: mandateForm.customer_account_number,
                        customer_account_type: mandateForm.customer_account_type,
                        destination_bank_id: mandateForm.destination_bank_id || undefined,
                        destination_bank_name: mandateForm.destination_bank_name || undefined
                    }
                })
            });
            const data = await response.json();
            if (data.success) {
                setMandateMessage('Mandate created successfully.');
                fetchLoans();
            } else {
                setMandateMessage(data.message || 'Failed to create mandate');
            }
        } catch (error) {
            console.error('Mandate create error:', error);
            setMandateMessage('Server error while creating mandate');
        } finally {
            setMandateLoading(false);
        }
    };

    return (
        <div className="loans-tab">
            <div className="super-card">
                <div className="super-card-header user-header">
                    <div className="header-left">
                        <h2>💰 Loans Management</h2>
                        <p className="header-sub">Search, filter, and manage loans or mandates.</p>
                    </div>
                    <div className="header-actions search-row">
                        <div className="pill-switch">
                            <button
                                className={`pill-btn ${activeLoanView === 'loans' ? 'active' : ''}`}
                                onClick={() => setActiveLoanView('loans')}
                            >
                                Loans
                            </button>
                            <button
                                className={`pill-btn ${activeLoanView === 'mandates' ? 'active' : ''}`}
                                onClick={() => setActiveLoanView('mandates')}
                            >
                                Mandates
                            </button>
                        </div>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by user, email, status..."
                            value={loanSearch}
                            onChange={(e) => setLoanSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && fetchLoans()}
                        />
                        <button className="secondary-btn" onClick={fetchLoans}>Search</button>
                        <select
                            className="admin-form-select"
                            style={{ width: '140px' }}
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Active">Active</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Closed">Closed</option>
                        </select>
                        <button className="primary-btn" onClick={fetchLoans}>Refresh</button>
                    </div>
                </div>

                <div className="super-table-container">
                    {activeLoanView === 'loans' ? (
                        <table className="super-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Amount</th>
                                    <th>Interest</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loans.length > 0 ? (
                                    loans.map(loan => (
                                        <tr key={loan.id}>
                                            <td>
                                                <div>{loan.first_name} {loan.last_name}</div>
                                                <div style={{ fontSize: '0.8em', color: '#888' }}>{loan.email}</div>
                                            </td>
                                            <td>₹{loan.amount}</td>
                                            <td>{loan.interest_rate}%</td>
                                            <td>
                                                <span className={`status-badge status-${loan.status?.toLowerCase()}`}>
                                                    {loan.status}
                                                </span>
                                                {(() => {
                                                    const err = extractMandateErrorInfo(loan.mandate_data);
                                                    if (!err) return null;
                                                    const label = err.description ? `${err.code} · ${err.description}` : err.code;
                                                    return (
                                                        <span className="mandate-error-chip" title={label}>
                                                            Mandate Error: {err.code}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td>{new Date(loan.created_at).toLocaleDateString()}</td>
                                            <td>
                                                <div className="action-buttons">
                                                    {hasPermission('loans.edit') && (
                                                        <button className="action-btn edit" onClick={() => openEditModal(loan)}>Edit</button>
                                                    )}
                                                    {loan.status === 'Pending' && hasPermission('loans.approve') && (
                                                        <>
                                                            <button
                                                                className="action-btn confirm"
                                                                onClick={() => handleUpdateStatus(loan.id, 'Active')}
                                                                title="Approve"
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                className="action-btn delete"
                                                                onClick={() => handleUpdateStatus(loan.id, 'Rejected')}
                                                                title="Reject"
                                                            >
                                                                ✕
                                                            </button>
                                                        </>
                                                    )}
                                                    {hasPermission('loans.delete') && (
                                                        <button
                                                            className="action-btn delete"
                                                            onClick={() => openDeleteModal(loan)}
                                                            title="Delete Permanent"
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                    <button className="action-btn view" onClick={() => openViewModal(loan)}>View</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                            {isLoading ? 'Loading loans...' : 'No loans found.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="super-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Loan</th>
                                    <th>Mandate ID</th>
                                    <th>Mandate Status</th>
                                    <th>Updated</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loans.length > 0 ? (
                                    loans.map(loan => {
                                        const done = isMandateDone(loan);
                                        return (
                                            <tr key={loan.id}>
                                                <td>
                                                    <div>{loan.first_name} {loan.last_name}</div>
                                                    <div style={{ fontSize: '0.8em', color: '#888' }}>{loan.email}</div>
                                                </td>
                                                <td>₹{loan.amount}</td>
                                                <td>{loan.mandate_id || '—'}</td>
                                                <td>
                                                    <span className={`status-badge ${done ? 'status-success' : 'status-warning'}`}>
                                                        {done ? 'Done' : 'Not Done'}
                                                    </span>
                                                    {loan.mandate_status && (
                                                        <span className="mandate-substatus">{loan.mandate_status}</span>
                                                    )}
                                                    {(() => {
                                                        const err = extractMandateErrorInfo(loan.mandate_data);
                                                        if (!err) return null;
                                                        const label = err.description ? `${err.code} · ${err.description}` : err.code;
                                                        return (
                                                            <span className="mandate-error-chip" title={label}>
                                                                Mandate Error: {err.code}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td>{new Date(loan.updated_at || loan.created_at).toLocaleDateString()}</td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button className="action-btn edit" onClick={() => openMandateModal(loan)} title="Manage Mandate">
                                                            Manage
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                            {isLoading ? 'Loading loans...' : 'No loans found.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {isEditModalOpen && selectedLoan && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content">
                        <h3>Edit Loan</h3>
                        <form onSubmit={handleEditSubmit}>
                            <div className="admin-form-group">
                                <label>Amount (₹)</label>
                                <input
                                    className="admin-form-input"
                                    type="number"
                                    value={editFormData.amount}
                                    onChange={e => setEditFormData({ ...editFormData, amount: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Interest Rate (%)</label>
                                <input
                                    className="admin-form-input"
                                    type="number"
                                    step="0.01"
                                    value={editFormData.interest_rate}
                                    onChange={e => setEditFormData({ ...editFormData, interest_rate: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Tenure (Months)</label>
                                <input
                                    className="admin-form-input"
                                    type="number"
                                    value={editFormData.tenure_months}
                                    onChange={e => setEditFormData({ ...editFormData, tenure_months: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Status</label>
                                <select
                                    className="admin-form-select"
                                    value={editFormData.status}
                                    onChange={e => setEditFormData({ ...editFormData, status: e.target.value })}
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="Active">Active (Approved)</option>
                                    <option value="Rejected">Rejected</option>
                                    <option value="Closed">Closed</option>
                                </select>
                            </div>
                            <div className="admin-modal-actions">
                                <button type="button" className="admin-modal-btn cancel" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                                <button type="submit" className="admin-modal-btn confirm">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isViewModalOpen && selectedLoan && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content">
                        <h3>Loan Details</h3>
                        <div className="admin-form-group">
                            <label>Applicant</label>
                            <p>{selectedLoan.first_name} {selectedLoan.last_name}</p>
                            <p style={{ fontSize: '0.9em', color: '#666' }}>{selectedLoan.email} | {selectedLoan.phone}</p>
                        </div>
                        <div className="admin-form-group">
                            <label>Loan Information</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div>
                                    <strong>Amount:</strong> ₹{selectedLoan.amount}
                                </div>
                                <div>
                                    <strong>Interest Rate:</strong> {selectedLoan.interest_rate}%
                                </div>
                                <div>
                                    <strong>Tenure:</strong> {selectedLoan.tenure_months} months
                                </div>
                                <div>
                                    <strong>Status:</strong> <span className={`status-badge status-${selectedLoan.status?.toLowerCase()}`}>{selectedLoan.status}</span>
                                </div>
                            </div>
                        </div>
                        <div className="admin-form-group">
                            <label>Applied On</label>
                            <p>{new Date(selectedLoan.created_at).toLocaleString()}</p>
                        </div>

                        <div className="admin-modal-actions">
                            <button className="admin-modal-btn cancel" onClick={() => setIsViewModalOpen(false)}>Close</button>
                            {selectedLoan.status === 'Pending' && hasPermission('loans.approve') && (
                                <>
                                    <button className="admin-modal-btn delete" onClick={() => handleUpdateStatus(selectedLoan.id, 'Rejected')}>Reject</button>
                                    <button className="admin-modal-btn confirm" onClick={() => handleUpdateStatus(selectedLoan.id, 'Active')}>Approve</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isMandateModalOpen && selectedLoan && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content mandate-modal">
                        <div className="mandate-header">
                            <div>
                                <h3>Create Mandate</h3>
                                <p>Loan #{selectedLoan.id} • {selectedLoan.first_name} {selectedLoan.last_name}</p>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    className="admin-modal-btn confirm"
                                    onClick={handleFetchMandateByCrn}
                                    disabled={mandateCrnLoading}
                                    title="Fetch mandate details using CRN"
                                >
                                    {mandateCrnLoading ? 'Fetching...' : 'Fetch By CRN'}
                                </button>
                                {selectedLoan.mandate_id && (
                                    <button
                                        type="button"
                                        className="admin-modal-btn confirm"
                                        onClick={handleFetchMandateDetails}
                                        disabled={mandateRefreshLoading}
                                    >
                                        {mandateRefreshLoading ? 'Refreshing...' : 'Refresh Mandate'}
                                    </button>
                                )}
                                <button type="button" className="admin-modal-btn cancel" onClick={() => setIsMandateModalOpen(false)}>Close</button>
                            </div>
                        </div>
                        {selectedLoan.mandate_id && (
                            <div className="mandate-status-bar">
                                <div>
                                    <strong>Mandate ID:</strong> {selectedLoan.mandate_id}
                                </div>
                                <div>
                                    <strong>Status:</strong> {selectedLoan.mandate_status || '—'}
                                </div>
                            </div>
                        )}
                        {(() => {
                            const err = extractMandateErrorInfo(mandateDetails || selectedLoan.mandate_data);
                            if (!err) return null;
                            return (
                                <div className="mandate-error-panel">
                                    <strong>Error Code:</strong> {err.code}
                                    {err.description && <span> — {err.description}</span>}
                                </div>
                            );
                        })()}
                        {mandateDetails && (
                            <div className="mandate-details">
                                <h4>Latest Mandate Details</h4>
                                <pre>{JSON.stringify(mandateDetails, null, 2)}</pre>
                            </div>
                        )}
                        {(() => {
                            const err = extractPresentationErrorInfo(debitDetails);
                            if (!err) return null;
                            return (
                                <div className="mandate-error-panel">
                                    <strong>Debit Error:</strong> {err.code}
                                    {err.description && <span> — {err.description}</span>}
                                </div>
                            );
                        })()}
                        <form onSubmit={handleCreateMandate}>
                            <div className="mandate-section">
                                <h4>Configuration</h4>
                                <div className="mandate-grid">
                                    <div className="admin-form-group">
                                        <label>Corporate Config ID</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.corporate_config_id}
                                            onChange={e => setMandateForm({ ...mandateForm, corporate_config_id: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Auth Mode</label>
                                        <select
                                            className="admin-form-select"
                                            value={mandateForm.auth_mode}
                                            onChange={e => setMandateForm({ ...mandateForm, auth_mode: e.target.value })}
                                        >
                                            <option value="API">API</option>
                                            <option value="ESIGN">ESIGN</option>
                                            <option value="PHYSICAL">PHYSICAL</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Notify Customer</label>
                                        <select
                                            className="admin-form-select"
                                            value={mandateForm.notify_customer ? 'true' : 'false'}
                                            onChange={e => setMandateForm({ ...mandateForm, notify_customer: e.target.value === 'true' })}
                                        >
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Notification Mode</label>
                                        <select
                                            className="admin-form-select"
                                            value={mandateForm.customer_notification_mode}
                                            onChange={e => setMandateForm({ ...mandateForm, customer_notification_mode: e.target.value })}
                                        >
                                            <option value="SMS">SMS</option>
                                            <option value="WHATSAPP">WhatsApp</option>
                                            <option value="ALL">All</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Include Auth URL</label>
                                        <select
                                            className="admin-form-select"
                                            value={mandateForm.include_authentication_url ? 'true' : 'false'}
                                            onChange={e => setMandateForm({ ...mandateForm, include_authentication_url: e.target.value === 'true' })}
                                        >
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Expire In Days</label>
                                        <input
                                            className="admin-form-input"
                                            type="number"
                                            min="1"
                                            max="90"
                                            value={mandateForm.expire_in_days}
                                            onChange={e => setMandateForm({ ...mandateForm, expire_in_days: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mandate-section">
                                <h4>Customer & Bank</h4>
                                <div className="mandate-grid">
                                    <div className="admin-form-group">
                                        <label>Customer Identifier (Email/Phone)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.customer_identifier}
                                            onChange={e => setMandateForm({ ...mandateForm, customer_identifier: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Customer Name</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.customer_name}
                                            onChange={e => setMandateForm({ ...mandateForm, customer_name: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Customer Ref Number</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.customer_ref_number}
                                            onChange={e => setMandateForm({ ...mandateForm, customer_ref_number: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Account Number</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.customer_account_number}
                                            onChange={e => setMandateForm({ ...mandateForm, customer_account_number: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Account Type</label>
                                        <select
                                            className="admin-form-select"
                                            value={mandateForm.customer_account_type}
                                            onChange={e => setMandateForm({ ...mandateForm, customer_account_type: e.target.value })}
                                        >
                                            <option value="SAVINGS">Savings</option>
                                            <option value="CURRENT">Current</option>
                                            <option value="OTHER">Other</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Destination Bank IFSC/MICR (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.destination_bank_id}
                                            onChange={e => setMandateForm({ ...mandateForm, destination_bank_id: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Destination Bank Name (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.destination_bank_name}
                                            onChange={e => setMandateForm({ ...mandateForm, destination_bank_name: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Live Banks Filter</label>
                                        <div className="bank-filter-row">
                                            <select
                                                className="admin-form-select"
                                                value={bankFilter.mode}
                                                onChange={e => setBankFilter({ ...bankFilter, mode: e.target.value })}
                                            >
                                                <option value="API">API</option>
                                                <option value="ESIGN">ESIGN</option>
                                                <option value="PHYSICAL">PHYSICAL</option>
                                            </select>
                                            <label className="bank-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={bankFilter.pennyLess}
                                                    onChange={e => setBankFilter({ ...bankFilter, pennyLess: e.target.checked })}
                                                />
                                                <span>Penny-less</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Search Bank</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={bankFilter.search}
                                            onChange={e => setBankFilter({ ...bankFilter, search: e.target.value })}
                                            placeholder="Type bank name or IFSC prefix"
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Live Banks</label>
                                        <div className="bank-select-row">
                                            <select
                                                className="admin-form-select"
                                                value={`${mandateForm.destination_bank_id}|${mandateForm.destination_bank_name}`}
                                                onChange={(e) => {
                                                    const [ifsc, name] = e.target.value.split('|');
                                                    setMandateForm({
                                                        ...mandateForm,
                                                        destination_bank_id: ifsc === 'NA' ? '' : ifsc,
                                                        destination_bank_name: name === 'NA' ? '' : name
                                                    });
                                                }}
                                            >
                                                <option value="NA|NA">Select bank</option>
                                                {banks
                                                    .filter(b => {
                                                        if (!bankFilter.search) return true;
                                                        const q = bankFilter.search.toLowerCase();
                                                        return (b.name || '').toLowerCase().includes(q) ||
                                                            (b.ifscPrefix || '').toLowerCase().includes(q);
                                                    })
                                                    .map(bank => (
                                                        <option key={bank.id} value={`${bank.ifscPrefix || ''}|${bank.name || ''}`}>
                                                            {bank.name} {bank.ifscPrefix ? `(${bank.ifscPrefix})` : ''}
                                                        </option>
                                                    ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="admin-modal-btn confirm"
                                                onClick={handleLoadBanks}
                                                disabled={banksLoading}
                                            >
                                                {banksLoading ? 'Loading...' : 'Load Banks'}
                                            </button>
                                        </div>
                                        {banksMessage && <p className="mandate-message">{banksMessage}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="mandate-section">
                                <h4>Schedule & Amount</h4>
                                <div className="mandate-grid">
                                    <div className="admin-form-group">
                                        <label>Management Category</label>
                                        <select
                                            className="admin-form-select"
                                            value={mandateForm.management_category}
                                            onChange={e => setMandateForm({ ...mandateForm, management_category: e.target.value })}
                                        >
                                            <option value="L001">L001 - Loan Installment</option>
                                            <option value="L002">L002 - Loan Amount Security</option>
                                            <option value="C001">C001 - Corporate</option>
                                            <option value="U099">U099 - Others</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Is Recurring</label>
                                        <select
                                            className="admin-form-select"
                                            value={mandateForm.is_recurring ? 'true' : 'false'}
                                            onChange={e => setMandateForm({ ...mandateForm, is_recurring: e.target.value === 'true' })}
                                        >
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                    </div>
                                    {mandateForm.is_recurring && (
                                        <div className="admin-form-group">
                                            <label>Frequency</label>
                                            <select
                                                className="admin-form-select"
                                                value={mandateForm.frequency}
                                                onChange={e => setMandateForm({ ...mandateForm, frequency: e.target.value })}
                                            >
                                                <option value="Monthly">Monthly</option>
                                                <option value="Weekly">Weekly</option>
                                                <option value="Daily">Daily</option>
                                                <option value="Quarterly">Quarterly</option>
                                                <option value="Yearly">Yearly</option>
                                                <option value="Adhoc">Adhoc</option>
                                            </select>
                                        </div>
                                    )}
                                    <div className="admin-form-group">
                                        <label>First Collection Date</label>
                                        <input
                                            className="admin-form-input"
                                            type="date"
                                            value={mandateForm.first_collection_date}
                                            onChange={e => setMandateForm({ ...mandateForm, first_collection_date: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Final Collection Date (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="date"
                                            value={mandateForm.final_collection_date}
                                            onChange={e => setMandateForm({ ...mandateForm, final_collection_date: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Collection Amount (₹)</label>
                                        <input
                                            className="admin-form-input"
                                            type="number"
                                            value={mandateForm.collection_amount}
                                            onChange={e => setMandateForm({ ...mandateForm, collection_amount: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Maximum Amount (₹) (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="number"
                                            value={mandateForm.maximum_amount}
                                            onChange={e => setMandateForm({ ...mandateForm, maximum_amount: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mandate-section">
                                <h4>Cancel Mandate</h4>
                                <div className="mandate-grid">
                                    <div className="admin-form-group">
                                        <label>Cancel Reason</label>
                                        <select
                                            className="admin-form-select"
                                            value={mandateForm.cancel_reason}
                                            onChange={e => setMandateForm({ ...mandateForm, cancel_reason: e.target.value })}
                                        >
                                            <option value="C001">C001 - Customer Request</option>
                                            <option value="C002">C002 - Corporate Request</option>
                                            <option value="C003">C003 - Account Closed/Frozen</option>
                                            <option value="C004">C004 - Bank Side</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>UMRN (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.cancel_umrn}
                                            onChange={e => setMandateForm({ ...mandateForm, cancel_umrn: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Destination IFSC (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={mandateForm.cancel_dest_ifsc}
                                            onChange={e => setMandateForm({ ...mandateForm, cancel_dest_ifsc: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="admin-modal-actions" style={{ marginTop: '16px' }}>
                                    <button
                                        type="button"
                                        className="admin-modal-btn delete-confirm"
                                        onClick={handleCancelMandate}
                                        disabled={mandateCancelLoading}
                                    >
                                        {mandateCancelLoading ? 'Cancelling...' : 'Cancel Mandate'}
                                    </button>
                                </div>
                            </div>

                            <div className="mandate-section">
                                <h4>Create Debit Request</h4>
                                <div className="mandate-grid">
                                    <div className="admin-form-group">
                                        <label>UMRN</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.umrn}
                                            onChange={e => setDebitForm({ ...debitForm, umrn: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Settlement Date</label>
                                        <input
                                            className="admin-form-input"
                                            type="date"
                                            value={debitForm.settlement_date}
                                            onChange={e => setDebitForm({ ...debitForm, settlement_date: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Corporate Account Number</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.corporate_account_number}
                                            onChange={e => setDebitForm({ ...debitForm, corporate_account_number: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Settlement Profile ID (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.settlement_config_profile_id}
                                            onChange={e => setDebitForm({ ...debitForm, settlement_config_profile_id: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Amount (₹)</label>
                                        <input
                                            className="admin-form-input"
                                            type="number"
                                            value={debitForm.amount}
                                            onChange={e => setDebitForm({ ...debitForm, amount: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Amount in Paise (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="number"
                                            value={debitForm.amount_in_paise}
                                            onChange={e => setDebitForm({ ...debitForm, amount_in_paise: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Frequency</label>
                                        <select
                                            className="admin-form-select"
                                            value={debitForm.frequency}
                                            onChange={e => setDebitForm({ ...debitForm, frequency: e.target.value })}
                                        >
                                            <option value="onetime">One Time</option>
                                            <option value="adhoc">Adhoc</option>
                                            <option value="intraday">Intra Day</option>
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="semiannually">Semi Annually</option>
                                            <option value="yearly">Yearly</option>
                                            <option value="bimonthly">Bi Monthly</option>
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Payment Count</label>
                                        <input
                                            className="admin-form-input"
                                            type="number"
                                            min="1"
                                            value={debitForm.payment_count}
                                            onChange={e => setDebitForm({ ...debitForm, payment_count: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Unique Key</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.unique_key}
                                            onChange={e => setDebitForm({ ...debitForm, unique_key: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Narration (optional)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.narration}
                                            onChange={e => setDebitForm({ ...debitForm, narration: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Plan ID (NSP)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.plan_id}
                                            onChange={e => setDebitForm({ ...debitForm, plan_id: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Reference ID (Unique Key)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.reference_id}
                                            onChange={e => setDebitForm({ ...debitForm, reference_id: e.target.value })}
                                        />
                                    </div>
                                </div>
                                {debitMessage && <p className="mandate-message">{debitMessage}</p>}
                                {debitFetchMessage && <p className="mandate-message">{debitFetchMessage}</p>}
                                {debitDetails && (
                                    <div className="mandate-details">
                                        <h4>Debit Schedule Response</h4>
                                        <pre>{JSON.stringify(debitDetails, null, 2)}</pre>
                                    </div>
                                )}
                                <div className="admin-modal-actions" style={{ marginTop: '16px' }}>
                                    <button
                                        type="button"
                                        className="admin-modal-btn confirm"
                                        onClick={handleCreateDebit}
                                        disabled={debitLoading}
                                    >
                                        {debitLoading ? 'Creating...' : 'Create Debit'}
                                    </button>
                                    <button
                                        type="button"
                                        className="admin-modal-btn cancel"
                                        onClick={handleFetchDebitDetails}
                                        disabled={debitFetchLoading}
                                    >
                                        {debitFetchLoading ? 'Fetching...' : 'Fetch Debit Details'}
                                    </button>
                                </div>
                            </div>

                            <div className="mandate-section">
                                <h4>Debit Plan Action</h4>
                                <div className="mandate-grid">
                                    <div className="admin-form-group">
                                        <label>Scheduled Plan ID</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.action_plan_id}
                                            onChange={e => setDebitForm({ ...debitForm, action_plan_id: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Action</label>
                                        <select
                                            className="admin-form-select"
                                            value={debitForm.action_type}
                                            onChange={e => setDebitForm({ ...debitForm, action_type: e.target.value })}
                                        >
                                            <option value="pause">Pause</option>
                                            <option value="resume">Resume</option>
                                            <option value="cancel">Cancel</option>
                                        </select>
                                    </div>
                                </div>
                                {debitActionMessage && <p className="mandate-message">{debitActionMessage}</p>}
                                <div className="admin-modal-actions" style={{ marginTop: '16px' }}>
                                    <button
                                        type="button"
                                        className="admin-modal-btn confirm"
                                        onClick={handleDebitPlanAction}
                                        disabled={debitActionLoading}
                                    >
                                        {debitActionLoading ? 'Submitting...' : 'Apply Action'}
                                    </button>
                                </div>
                            </div>

                            <div className="mandate-section">
                                <h4>Transaction Action</h4>
                                <div className="mandate-grid">
                                    <div className="admin-form-group">
                                        <label>Transaction ID (NPT)</label>
                                        <input
                                            className="admin-form-input"
                                            type="text"
                                            value={debitForm.transaction_id}
                                            onChange={e => setDebitForm({ ...debitForm, transaction_id: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Action</label>
                                        <select
                                            className="admin-form-select"
                                            value={debitForm.transaction_action}
                                            onChange={e => setDebitForm({ ...debitForm, transaction_action: e.target.value })}
                                        >
                                            <option value="cancel">Cancel</option>
                                            <option value="reattempt">Reattempt</option>
                                            <option value="update">Update</option>
                                            <option value="approve">Approve</option>
                                        </select>
                                    </div>
                                </div>
                                {transactionActionMessage && <p className="mandate-message">{transactionActionMessage}</p>}
                                {transactionFetchMessage && <p className="mandate-message">{transactionFetchMessage}</p>}
                                <div className="admin-modal-actions" style={{ marginTop: '16px' }}>
                                    <button
                                        type="button"
                                        className="admin-modal-btn confirm"
                                        onClick={handleTransactionAction}
                                        disabled={transactionActionLoading}
                                    >
                                        {transactionActionLoading ? 'Submitting...' : 'Apply Transaction Action'}
                                    </button>
                                    <button
                                        type="button"
                                        className="admin-modal-btn cancel"
                                        onClick={handleFetchTransactionDetails}
                                        disabled={transactionFetchLoading}
                                    >
                                        {transactionFetchLoading ? 'Fetching...' : 'Fetch Transaction Details'}
                                    </button>
                                </div>
                            </div>

                            {mandateMessage && <p className="mandate-message">{mandateMessage}</p>}
                            <div className="admin-modal-actions">
                                <button type="button" className="admin-modal-btn cancel" onClick={() => setIsMandateModalOpen(false)}>Cancel</button>
                                <button type="submit" className="admin-modal-btn confirm" disabled={mandateLoading}>
                                    {mandateLoading ? 'Creating...' : 'Create Mandate'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirmation Modal */}
            {isDeleteModalOpen && loanToDelete && (
                <div className="admin-modal-overlay" style={{ zIndex: 10000 }}>
                    <div className="admin-modal-content delete-confirmation-modal" style={{ maxWidth: '500px' }}>
                        <div className="delete-modal-header">
                            <div className="warning-icon">⚠️</div>
                            <h3>Delete Loan Confirmation</h3>
                        </div>

                        <div className="delete-modal-body">
                            <p className="warning-text">You are about to <strong>PERMANENTLY DELETE</strong> this loan from the system.</p>

                            <div className="loan-info-box">
                                <h4>Loan Details:</h4>
                                <p><strong>Client:</strong> {loanToDelete.first_name} {loanToDelete.last_name}</p>
                                <p><strong>Amount:</strong> ₹{loanToDelete.amount}</p>
                                <p><strong>Status:</strong> {loanToDelete.status}</p>
                            </div>

                            <div className="warning-box">
                                <p className="warning-title">⚠️ This will delete ALL loan information including:</p>
                                <ul className="warning-list">
                                    <li>Loan amount and terms</li>
                                    <li>Payment history</li>
                                    <li>Interest calculations</li>
                                    <li>Associated documents</li>
                                    <li>Client loan records</li>
                                </ul>
                                <p className="danger-text">⛔ This action CANNOT be undone!</p>
                            </div>
                        </div>

                        <div className="admin-modal-actions">
                            <button
                                type="button"
                                className="admin-modal-btn cancel"
                                onClick={() => {
                                    setIsDeleteModalOpen(false);
                                    setLoanToDelete(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="admin-modal-btn delete-confirm"
                                onClick={handleDeleteLoan}
                            >
                                🗑️ Yes, Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const initialFormState = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: '',
    status: 'new',
    notes: '',
    assigned_to: ''
};

export function LeadsTab({ adminSession }) {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState([]);
    const [assignees, setAssignees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [assigneeFilter, setAssigneeFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
    const [editingLead, setEditingLead] = useState(null);
    const [formData, setFormData] = useState(initialFormState);

    const hasPermission = (perm) => adminSession?.role === 'super_admin' || adminSession?.permissions?.includes(perm);

    useEffect(() => {
        fetchLeads();
        fetchStats();
        fetchAssignees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchLeads = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/leads`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) setLeads(data.data);
        } catch (e) { console.error(e); }
    };

    const fetchStats = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/leads/stats`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) setStats(data.data);
        } catch (e) { console.error(e); }
    };

    const fetchAssignees = async () => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/admins`, {
                headers: { 'x-user-id': adminSession?.userId }
            });
            const data = await res.json();
            if (data.success) setAssignees(data.data);
        } catch (e) { console.error(e); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const url = editingLead
            ? `${API_CONFIG.BASE_URL}/api/admin/leads/${editingLead.id}`
            : `${API_CONFIG.BASE_URL}/api/admin/leads`;
        const method = editingLead ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify(formData)
            });
            const data = await res.json();
            if (data.success) {
                setIsModalOpen(false);
                setEditingLead(null);
                setFormData({ first_name: '', last_name: '', email: '', phone: '', source: '', status: 'new', notes: '', assigned_to: '' });
                fetchLeads();
                fetchStats();
            } else {
                alert(data.message);
            }
        } catch (e) { alert('Error saving lead'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this lead?')) return;
        try {
            await fetch(`${API_CONFIG.BASE_URL}/api/admin/leads/${id}`, {
                method: 'DELETE',
                headers: { 'x-user-id': adminSession?.userId }
            });
            fetchLeads();
            fetchStats();
        } catch (e) { alert('Error deleting lead'); }
    };

    const openEdit = (lead) => {
        setEditingLead(lead);
        setFormData({ ...lead, assigned_to: lead.assigned_to || '' });
        setIsModalOpen(true);
    };

    const handleQuickAssign = async (lead) => {
        if (!hasPermission('leads.edit')) return;
        const payload = { ...lead, assigned_to: adminSession?.userId };
        try {
            await fetch(`${API_CONFIG.BASE_URL}/api/admin/leads/${lead.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify(payload)
            });
            fetchLeads();
        } catch (e) { alert('Failed to assign'); }
    };

    const handleStatusChange = async (lead, newStatus) => {
        if (!hasPermission('leads.edit')) return;
        const payload = { ...lead, status: newStatus };
        try {
            await fetch(`${API_CONFIG.BASE_URL}/api/admin/leads/${lead.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-id': adminSession?.userId },
                body: JSON.stringify(payload)
            });
            fetchLeads();
            fetchStats();
        } catch (e) { alert('Failed to update status'); }
    };

    const normalizedStats = stats.reduce((acc, s) => ({ ...acc, [s.status]: s.count }), {});
    const totalLeads = stats.reduce((sum, s) => sum + s.count, 0);
    const convertedCount = normalizedStats.converted || 0;
    const conversionRate = totalLeads ? Math.round((convertedCount / totalLeads) * 100) : 0;

    const filteredLeads = leads
        .filter(l => {
            const term = searchTerm.toLowerCase();
            const matchesSearch = !term || [
                l.first_name, l.last_name, l.email, l.phone, l.source, l.status
            ].some(v => (v || '').toLowerCase().includes(term));

            const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
            const matchesSource = sourceFilter === 'all' || l.source === sourceFilter;
            const matchesAssignee = assigneeFilter === 'all' ||
                (assigneeFilter === 'unassigned' && !l.assigned_to) ||
                `${l.assigned_to}` === assigneeFilter;

            return matchesSearch && matchesStatus && matchesSource && matchesAssignee;
        })
        .sort((a, b) => {
            if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
            if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
            if (sortBy === 'source') return (a.source || '').localeCompare(b.source || '');
            if (sortBy === 'unassigned') return (!!a.assigned_to) - (!!b.assigned_to);
            return new Date(b.created_at) - new Date(a.created_at); // newest
        });

    const statusOptions = [
        { value: 'new', label: 'New' },
        { value: 'contacted', label: 'Contacted' },
        { value: 'qualified', label: 'Qualified' },
        { value: 'converted', label: 'Converted' },
        { value: 'lost', label: 'Lost' }
    ];

    const sourceOptions = ['Website', 'Referral', 'Social Media', 'Direct', 'Event'];

    const renderStatusPill = (status) => (
        <span className={`status-pill status-${status}`}>
            {statusOptions.find(s => s.value === status)?.label || status}
        </span>
    );

    // --- Drag and Drop Logic ---
    const handleDragStart = (e, leadId) => {
        e.dataTransfer.setData('leadId', leadId);
        e.target.classList.add('dragging');
    };

    const handleDragEnd = (e) => {
        e.target.classList.remove('dragging');
        document.querySelectorAll('.kanban-column').forEach(col => col.classList.remove('drag-over'));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        const column = e.target.closest('.kanban-column');
        if (column) column.classList.add('drag-over');
    };

    const handleDragLeave = (e) => {
        const column = e.target.closest('.kanban-column');
        if (column) column.classList.remove('drag-over');
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        const leadId = e.dataTransfer.getData('leadId');
        document.querySelectorAll('.kanban-column').forEach(col => col.classList.remove('drag-over'));

        if (!leadId) return;

        // Optimistic Update
        const lead = leads.find(l => l.id === parseInt(leadId));
        if (lead && lead.status !== newStatus) {
            handleStatusChange(lead, newStatus);
        }
    };
    // --- End Drag and Drop ---

    const renderAssignee = (lead) => lead.assigned_to_first
        ? `${lead.assigned_to_first} ${lead.assigned_to_last || ''}`.trim()
        : 'Unassigned';

    const handleWhatsapp = (lead) => {
        if (!lead.phone) return alert('No phone number available');
        const phone = lead.phone.replace(/\D/g, '');
        window.open(`https://wa.me/${phone}?text=Hi ${lead.first_name}, regarding your loan inquiry...`, '_blank');
    };

    const handleCall = (lead) => {
        if (!lead.phone) return alert('No phone number available');
        window.location.href = `tel:${lead.phone}`;
    };

    return (
        <div className="leads-tab">
            <div className="super-card">
                <div className="super-card-header user-header">
                    <div className="header-left">
                        <h2>🎯 Leads Command Center</h2>
                        <p className="header-sub">Track pipeline health, assign quickly, and move leads faster.</p>
                    </div>
                    <div className="header-actions">
                        <div className="leads-view-toggle">
                            <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>List</button>
                            <button className={`view-btn ${viewMode === 'kanban' ? 'active' : ''}`} onClick={() => setViewMode('kanban')}>Kanban</button>
                        </div>
                        <button className="secondary-btn" onClick={fetchLeads}>Refresh</button>
                        {hasPermission('leads.create') && (
                            <button className="primary-btn" onClick={() => { setEditingLead(null); setFormData(initialFormState); setIsModalOpen(true); }} style={{ padding: '8px 16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>+</span> Add Lead
                            </button>
                        )}
                    </div>
                </div>

                <div className="leads-dashboard-container">
                    {/* 1. Header & Stats */}
                    <div className="leads-header-section">
                        <div className="leads-insights-row">
                            <div className="mini-kpi purple">
                                <div className="kpi-header">
                                    <span>Total Leads</span>
                                    <span className="kpi-trend up">↗ 12%</span>
                                </div>
                                <div className="kpi-body">
                                    <strong>{totalLeads}</strong>
                                    <div className="kpi-chart">
                                        <svg width="100%" height="40" viewBox="0 0 100 40" preserveAspectRatio="none">
                                            <defs>
                                                <linearGradient id="gradPurple" x1="0%" y1="0%" x2="0%" y2="100%">
                                                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.2" />
                                                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                                                </linearGradient>
                                            </defs>
                                            <path d="M0,35 Q10,35 20,25 T40,20 T60,30 T80,15 T100,5" fill="none" stroke="#8b5cf6" strokeWidth="2" />
                                            <path d="M0,35 Q10,35 20,25 T40,20 T60,30 T80,15 T100,5 V40 H0 Z" fill="url(#gradPurple)" stroke="none" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="mini-kpi blue">
                                <div className="kpi-header">
                                    <span>Qualified</span>
                                    <span className="kpi-trend">Active</span>
                                </div>
                                <div className="kpi-body">
                                    <strong>{normalizedStats.qualified || 0}</strong>
                                    <div className="kpi-chart donut">
                                        <svg width="40" height="40" viewBox="0 0 36 36">
                                            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                                            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#3b82f6" strokeWidth="3" strokeDasharray={`${Math.min((normalizedStats.qualified / totalLeads) * 100 || 0, 100)}, 100`} />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="mini-kpi green">
                                <div className="kpi-header">
                                    <span>Conversion Rate</span>
                                    <span className="kpi-trend up">Success</span>
                                </div>
                                <div className="kpi-body">
                                    <strong>{conversionRate}%</strong>
                                    <div className="kpi-chart bars">
                                        <div className="bar" style={{ height: '40%' }}></div>
                                        <div className="bar" style={{ height: '70%' }}></div>
                                        <div className="bar active" style={{ height: `${conversionRate}%` }}></div>
                                        <div className="bar" style={{ height: '30%' }}></div>
                                    </div>
                                </div>
                            </div>
                            <div className="mini-kpi amber">
                                <div className="kpi-header">
                                    <span>Unassigned</span>
                                    {parseInt(leads.filter(l => !l.assigned_to).length) > 0 && <span className="kpi-alert">Action Needed</span>}
                                </div>
                                <div className="kpi-body">
                                    <strong>{leads.filter(l => !l.assigned_to).length}</strong>
                                    <div className="kpi-icon-bg">
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" opacity="0.2">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. Controls Toolbar */}
                    <div className="leads-toolbar" style={{ display: 'flex', gap: '16px', justifyContent: 'space-between', padding: '16px 24px' }}>
                        <div className="search-stack" style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
                            <div className="search-icon-placeholder" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Auto Search leads..."
                                className="search-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '10px 10px 10px 36px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                            />
                        </div>

                        <div className="filter-group" style={{ display: 'flex', gap: '12px' }}>
                            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="all">All Status</option>
                                {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>

                            <select className="filter-select" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
                                <option value="all">All Sources</option>
                                {sourceOptions.map(src => <option key={src} value={src}>{src}</option>)}
                            </select>

                            <select className="filter-select" value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
                                <option value="all">All Assignees</option>
                                <option value="unassigned">Unassigned</option>
                                {assignees.map(a => (
                                    <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
                                ))}
                            </select>

                            <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="status">Sort by Status</option>
                            </select>
                        </div>
                    </div>

                    {/* 3. Quick Pills */}


                    {/* 4. Kanban/List Content */}
                    {viewMode === 'kanban' ? (
                        <div className="kanban-board-container">
                            <div className="kanban-board">
                                {statusOptions.map(opt => {
                                    const items = filteredLeads.filter(l => l.status === opt.value);
                                    return (
                                        <div
                                            key={opt.value}
                                            className={`kanban-column col-${opt.value}`}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, opt.value)}
                                        >
                                            <div className="kanban-header">
                                                <span>{opt.label}</span>
                                                <span className="kanban-count">{items.length}</span>
                                            </div>
                                            <div className="kanban-cards">
                                                {items.map(lead => (
                                                    <div
                                                        key={lead.id}
                                                        className="kanban-card"
                                                        draggable={hasPermission('leads.edit')}
                                                        onDragStart={(e) => handleDragStart(e, lead.id)}
                                                        onDragEnd={handleDragEnd}
                                                    >
                                                        <div className="k-card-top">
                                                            <div className="k-name">{lead.first_name} {lead.last_name}</div>
                                                            <div className="k-date">{new Date(lead.created_at).toLocaleDateString()}</div>
                                                        </div>
                                                        {lead.source && <span className="k-source">{lead.source}</span>}
                                                        <div className="k-details">
                                                            {lead.phone && <div>📞 {lead.phone}</div>}
                                                            {lead.email && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>✉️ {lead.email}</div>}
                                                        </div>
                                                        <div className="k-footer">
                                                            <div className="k-assignee">
                                                                {lead.assigned_to_first ?
                                                                    <span>👤 {lead.assigned_to_first}</span> :
                                                                    <span style={{ color: '#f59e0b' }}>⚠️ Unassigned</span>
                                                                }
                                                            </div>
                                                            <div className="k-actions">
                                                                <button className="icon-btn call" onClick={() => handleCall(lead)} title="Call">
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                                                    </svg>
                                                                </button>
                                                                <button className="icon-btn whatsapp" onClick={() => handleWhatsapp(lead)} title="WhatsApp">
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                                                    </svg>
                                                                </button>
                                                                <button className="icon-btn edit" onClick={() => openEdit(lead)} title="Edit">
                                                                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="super-table-container">
                            <table className="super-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Contact</th>
                                        <th>Source</th>
                                        <th>Status</th>
                                        <th>Assigned</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLeads.map(lead => (
                                        <tr key={lead.id}>
                                            <td>
                                                <div className="lead-name">{lead.first_name} {lead.last_name}</div>
                                                <div className="lead-id">ID #{lead.id}</div>
                                            </td>
                                            <td>
                                                <div className="contact-line">{lead.email}</div>
                                                <div className="contact-line light">{lead.phone || 'No phone'}</div>
                                            </td>
                                            <td>{lead.source || '—'}</td>
                                            <td>{renderStatusPill(lead.status)}</td>
                                            <td className={lead.assigned_to ? '' : 'muted'}>{renderAssignee(lead)}</td>
                                            <td>{new Date(lead.created_at).toLocaleDateString()}</td>
                                            <td>
                                                <div className="action-buttons tight">
                                                    <button className="icon-btn call" onClick={() => handleCall(lead)} title="Call">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                                        </svg>
                                                    </button>
                                                    <button className="icon-btn whatsapp" onClick={() => handleWhatsapp(lead)} title="WhatsApp">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                                        </svg>
                                                    </button>
                                                    {hasPermission('leads.edit') && (
                                                        <button className="icon-btn edit" onClick={() => openEdit(lead)} title="Edit">
                                                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                        </button>
                                                    )}
                                                    {hasPermission('leads.edit') && !lead.assigned_to && (
                                                        <button className="icon-btn assign" onClick={() => handleQuickAssign(lead)} title="Assign">
                                                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                                                        </button>
                                                    )}
                                                    {hasPermission('leads.delete') && (
                                                        <button className="icon-btn delete" onClick={() => handleDelete(lead.id)} title="Delete">
                                                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredLeads.length === 0 && (
                                        <tr>
                                            <td colSpan="7" style={{ textAlign: 'center', padding: '18px', color: '#94a3b8' }}>
                                                No leads match the current filters.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}


                    {
                        isModalOpen && (
                            <div className="leads-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setIsModalOpen(false) }}>
                                <div className="leads-modal-content">
                                    <div className="leads-modal-header">
                                        <h3>{editingLead ? '✏️ Edit Lead Details' : '✨ Add New Lead'}</h3>
                                    </div>
                                    <form onSubmit={handleSubmit}>
                                        <div className="leads-modal-body">
                                            <div className="modal-grid-row">
                                                <div className="fancy-input-group">
                                                    <label className="fancy-label">First Name</label>
                                                    <input className="fancy-input" placeholder="e.g. John" value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} required />
                                                </div>
                                                <div className="fancy-input-group">
                                                    <label className="fancy-label">Last Name</label>
                                                    <input className="fancy-input" placeholder="e.g. Doe" value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} />
                                                </div>
                                            </div>

                                            <div className="modal-grid-row">
                                                <div className="fancy-input-group">
                                                    <label className="fancy-label">Email Address</label>
                                                    <input className="fancy-input" placeholder="john@example.com" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                                                </div>
                                                <div className="fancy-input-group">
                                                    <label className="fancy-label">Phone Number</label>
                                                    <input className="fancy-input" placeholder="+91 98765 43210" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                                                </div>
                                            </div>

                                            <div className="modal-grid-row">
                                                <div className="fancy-input-group">
                                                    <label className="fancy-label">Lead Source</label>
                                                    <select className="fancy-select" value={formData.source} onChange={e => setFormData({ ...formData, source: e.target.value })}>
                                                        <option value="">Select Source...</option>
                                                        <option value="Website">Website</option>
                                                        <option value="Referral">Referral</option>
                                                        <option value="Social Media">Social Media</option>
                                                        <option value="Direct">Direct</option>
                                                        <option value="Event">Event</option>
                                                    </select>
                                                </div>
                                                <div className="fancy-input-group">
                                                    <label className="fancy-label">Current Status</label>
                                                    <select className="fancy-select" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                                        <option value="new">New</option>
                                                        <option value="contacted">Contacted</option>
                                                        <option value="qualified">Qualified</option>
                                                        <option value="lost">Lost</option>
                                                        <option value="converted">Converted</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="fancy-input-group">
                                                <label className="fancy-label">Assign To</label>
                                                <select className="fancy-select" value={formData.assigned_to} onChange={e => setFormData({ ...formData, assigned_to: e.target.value })}>
                                                    <option value="">-- Unassigned --</option>
                                                    {assignees.filter(a => a.role_name !== 'super_admin').map(a => (
                                                        <option key={a.id} value={a.id}>{a.first_name} {a.last_name} ({a.role_name})</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="fancy-input-group" style={{ marginTop: '16px' }}>
                                                <label className="fancy-label">Internal Notes</label>
                                                <textarea className="fancy-textarea" placeholder="Add any relevant details about this lead..." value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows="3"></textarea>
                                            </div>
                                        </div>

                                        <div className="modal-footer">
                                            <button type="button" className="secondary-btn" onClick={() => setIsModalOpen(false)} style={{ background: '#f1f5f9', color: '#64748b' }}>Cancel</button>
                                            <button type="submit" className="primary-btn">Save Lead</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
}
